import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Form, Input, Button, message, Table, Select, Tooltip,
  Card,
  Row,
  Col,
  Typography,
  Space,
  Tag,
  DatePicker,
  Modal,
  Popover,
  Badge,
  Checkbox,
  Descriptions,
} from 'antd';
import {
  InfoCircleOutlined,
  SendOutlined,
  UserOutlined,
  FileProtectOutlined,
  TruckOutlined,
  FileTextOutlined,
  GlobalOutlined,
  ContainerOutlined,
  ExclamationCircleOutlined,
  TrademarkCircleFilled,
  DeleteFilled,
  CheckCircleFilled,
  BellOutlined,
  CheckOutlined,
  CopyOutlined,
  FilterOutlined,
  SearchOutlined,
  SaveOutlined,
  CloseOutlined,
  SecurityScanOutlined,
  LeftOutlined,
  TableOutlined,
  PaperClipOutlined,
  MailOutlined,
} from '@ant-design/icons';
import './index.css';
import apiClient, { getOpInfoList } from '../../utils/apiService';
import authApi from '../../api/auth';
import { workorderApi } from '../../api/workorder';
import { trackWorkorderSearch } from '../../utils/tracker';
import TableColumnDrawer from './components/TableColumnDrawer';
import WorkOrderAttachmentDrawer from './components/WorkOrderAttachmentDrawer';
import { DISPLAY_DATE_FORMAT, formatDisplayDate } from '../../utils/dateDisplay';
import { getReadScope } from '../../utils/sharedReadCache';
import ViewFileModal from '../../components/viewfilemodal';
import dayjs from 'dayjs';


const { Text } = Typography;
const QUERY_MODEL_SEARCH = 1;
const QUERY_MODEL_QUICK = 2;
const QUICK_SEARCH_STORAGE_KEY = 'primeocr.workorder.quick-search.v1';

const getStoredQuickSearchState = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(QUICK_SEARCH_STORAGE_KEY) || 'null');
    const history = Array.isArray(stored?.history) ? stored.history : [];
    const index = Math.min(Math.max(Number(stored?.index) || -1, -1), history.length - 1);

    return {
      history: history.map((entry) => {
        const formValues = { ...(entry?.formValues || {}) };
        ['fromDate', 'toDate'].forEach((key) => {
          if (formValues[key] && !dayjs.isDayjs(formValues[key])) {
            const parsed = dayjs(formValues[key]);
            formValues[key] = parsed.isValid() ? parsed : undefined;
          }
        });
        return { ...entry, formValues };
      }),
      index,
    };
  } catch (error) {
    return { history: [], index: -1 };
  }
};

const getStoredUserInfo = () => {
  const storageValues = [localStorage.getItem('userInfo'), sessionStorage.getItem('userInfo')];

  for (const storageValue of storageValues) {
    if (!storageValue) continue;

    try {
      return JSON.parse(storageValue);
    } catch (error) {
      console.error('Failed to parse stored user info:', error);
    }
  }

  return {};
};

const QUICK_SEARCH_TYPE_MAP = {
  hblNo: 'HBL',
  mblNo: 'MBL',
  containerNo: 'CONTAINER',
  bookingNo: 'BK No',
  anyWord: 'anyWord',
  carrier: 'CARRIER',
  eta: 'ETA',
  etd: 'ETD',
  pic: 'PIC',
  portOfLoading: 'PORT OF LOADING',
  portOfDischarge: 'PORT OF DISCHARGE',
  placeOfDelivery: 'PLACE OF DELIVERY',
  totalPackages: 'TOTAL PKGS',
  totalCc: 'TOTAL CC',
  vesselName: 'VESSEL NAME',
  voyage: 'VOYAGE',
  cashierType: 'CASHIER',
  mblCc: 'MBL CC',
  serialNo: 'SERIAL NUMBER',
  primeNote: 'PRIME NOTE',
  originNote: 'ORIGIN NOTE',
  customerNote: 'CUSTOMER NOTE',
};

const QUICK_SEARCH_TYPE_LABELS = {
  hblNo: 'HBL',
  mblNo: 'MBL',
  containerNo: 'Container No',
  bookingNo: 'BK No.',
  anyWord: 'Any Word',
  carrier: 'Carrier',
  eta: 'ETA',
  etd: 'ETD',
  pic: 'PIC',
  portOfLoading: 'Port of Loading',
  portOfDischarge: 'Port of Discharge',
  placeOfDelivery: 'Place of Delivery',
  totalPackages: 'Total PKGS',
  totalCc: 'Total CC',
  vesselName: 'Vessel Name',
  voyage: 'Voyage',
  cashierType: 'Cashier',
  mblCc: 'MBL CC',
  serialNo: 'Serial Number',
  primeNote: 'Prime Note',
  originNote: 'Origin Note',
  customerNote: 'Customer Note',
};

const QUICK_SEARCH_STATUS_MAP = {
  ZZ: 'ZZ',
  Transmitted: 'TRANSMITTED',
  Process: 'PROCESSED',
  released: 'RELEASED',
  onHold: 'ON HOLD',
  overdue: 'OVERDUE',
  anSent: 'A/N SENT',
  anFailed: 'AN FAILED',
  hblOriginal: 'HBL ORIGINAL',
  hblTelex: 'HBL TELEX',
  manifestRcvd: 'MANIFEST RCVD',
  mblOriginal: 'MBL ORIGINAL',
  mblTelex: 'MBL TELEX',
  onBcList: 'ON BC LIST',
};

const QUICK_SEARCH_STATUS_LABELS = {
  ZZ: 'ZZ',
  Transmitted: 'TRANSMITTED',
  Process: 'PROCESSED',
  released: 'RELEASED',
  onHold: 'ON HOLD',
  overdue: 'OVERDUE',
  anSent: 'A/N SENT',
  anFailed: 'AN FAILED',
  hblOriginal: 'HBL ORIGINAL',
  hblTelex: 'HBL TELEX',
  manifestRcvd: 'MANIFEST RCVD',
  mblOriginal: 'MBL ORIGINAL',
  mblTelex: 'MBL TELEX',
  onBcList: 'ON BC LIST',
};

const QUICK_SEARCH_NO_INFO_MAP = {
  noAnDate: 1,
  noEtaDate: 2,
  noReleaseDate: 3,
  noPickup: 4,
  noIt: 5,
  noFreightLocation: 6,
  noEmlAddress: 7,
  noPic: 8,
};

const QUICK_SEARCH_NO_INFO_LABELS = {
  1: 'NO A/N DATE',
  2: 'NO ETA DATE',
  3: 'NO RELEASE DATE',
  4: 'NO PICK-UP#',
  5: 'NO IT#',
  6: 'NO FREIGHT LOCATION',
  7: 'NO EML ADDRESS',
  8: 'NO PIC',
};

const QUICK_SEARCH_SOURCE_MAP = {
  pai: 1,
  pms: 2,
  all: 3,
};

const QUICK_SEARCH_SOURCE_LABELS = {
  pai: 'PAI',
  pms: 'PMS',
  all: 'ALL',
};

const normalizeQuickSearchNoInfo = (value) => {
  if (value == null || value === '') return undefined;
  if (typeof value === 'number' && value >= 1 && value <= 8) return value;
  const str = String(value).trim();
  if (/^[1-8]$/.test(str)) return Number(str);
  return QUICK_SEARCH_NO_INFO_MAP[str];
};

// emptyEmailflag: 1=筛选邮箱为空，0 或不传=全部
const getEmptyEmailflag = (val) => {
  if (val == null || val === '') return undefined;
  return Number(val) === 1 ? 1 : undefined;
};

const WORKORDER_TABLE_COLUMN_CONFIG_KEY = 'workorder_table_column_config';
const getColumnConfigStorageKey = (username) => `${WORKORDER_TABLE_COLUMN_CONFIG_KEY}_${username || 'anonymous'}`;
const LOCKED_COLUMN_KEYS = new Set(['rowNo', 'hblNo', 'action', 'attachments']);
const COLUMN_WIDTH_MIN = 40;
const COLUMN_WIDTH_MAX = 2000;
const COLUMN_WIDTH_CONFIGURABLE_FIELDS = new Set([
  'seq', 'autoSerialNo', 'hblNo', 'mblNo', 'consigneeCompany', 'nominated', 'salesman', 'pic', 'email',
  'forwardingRecord', 'etd', 'eta', 'eta_port_update', 'vesselName', 'voyage', 'portOfDischarge',
  'placeOfDelivery', 'status', 'transmittedTime', 'pickupNumberStatus', 'freeStorageDays',
  'freeContainerDays', 'overduePickupDays', 'overdueReturnDays', 'blType', 'pendingFees', 'anStatus',
  'anSendStatus', 'puSendStatus', 'primeNote', 'blacklistFlag', 'cashierType', 'sourceFlag',
  'notifications', 'containers', 'containerNumbers', 'documentPaths', 'firstDocumentPath', 'origin_note',
]);
const COLUMN_FIELD_MAP = {
  rowNo: 'seq',
  hblNo: 'hblNo',
  mblNo: 'mblNo',
  consigneeCompany: 'consigneeCompany',
  pic: 'pic',
  containerNumbers: 'containerNumbers',
  portOfDischarge: 'portOfDischarge',
  placeOfDelivery: 'placeOfDelivery',
  primeNote: 'primeNote',
  origin_note: 'origin_note',
  eta_port_update: 'eta_port_update',
  vesselName: 'vesselName',
  voyage: 'voyage',
  etd: 'etd',
  status: 'status',
  sourceFlag: 'sourceFlag',
  transmittedTime: 'transmittedTime',
  forwardingRecord: 'forwardingRecord',
  pickupNumberStatus: 'pickupNumberStatus',
  freeStorageDays: 'freeStorageDays',
  freeContainerDays: 'freeContainerDays',
  overduePickupDays: 'overduePickupDays',
  overdueReturnDays: 'overdueReturnDays',
  blType: 'blType',
  cashierType: 'cashierType',
  pendingFees: 'pendingFees',
  anStatus: 'anStatus',
  documentPaths: 'documentPaths',
  action: null,
  attachments: null,
};
const COLUMN_FIELD_DEPENDENCIES = {
  rowNo: ['seq'],
  hblNo: ['hblNo', 'containers', 'blacklistFlag', 'anSendStatus', 'puSendStatus'],
  pic: ['seq', 'pic'],
  containerNumbers: ['seq', 'containerNumbers', 'containers', 'hblNo', 'pic'],
  primeNote: ['seq', 'primeNote', 'pic'],
  origin_note: ['origin_note'],
  eta_port_update: ['seq', 'eta_port_update', 'pic'],
  forwardingRecord: ['seq', 'forwardingRecord'],
  action: ['seq', 'hblNo', 'notifications'],
  attachments: ['seq', 'hblNo'],
};

const clampColumnWidth = (width) => Math.min(
  COLUMN_WIDTH_MAX,
  Math.max(COLUMN_WIDTH_MIN, Math.round(Number(width) || COLUMN_WIDTH_MIN))
);

const ResizableHeaderCell = ({ children, width, onResize, ...cellProps }) => {
  const handleMouseDown = (event) => {
    if (!onResize) return;
    event.preventDefault();
    event.stopPropagation();

    const startX = event.clientX;
    const startWidth = Number(width) || COLUMN_WIDTH_MIN;
    const onMouseMove = (moveEvent) => onResize(clampColumnWidth(startWidth + moveEvent.clientX - startX));
    const onMouseUp = (upEvent) => {
      onResize(clampColumnWidth(startWidth + upEvent.clientX - startX));
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  return (
    <th {...cellProps} style={{ ...cellProps.style, position: 'relative', width }}>
      {children}
      {onResize && <span className="workorder-column-resize-handle" onMouseDown={handleMouseDown} />}
    </th>
  );
};

const getColumnConfigLabel = (title, key) => {
  if (typeof title === 'string' && title.trim()) return title;
  return key;
};

const buildDefaultColumnConfig = (columns) => columns.map((column, index) => {
  const key = column.key || column.dataIndex || `column-${index}`;
  return {
    key,
    title: getColumnConfigLabel(column.title, key),
    visible: true,
    locked: LOCKED_COLUMN_KEYS.has(key),
    field: Object.prototype.hasOwnProperty.call(COLUMN_FIELD_MAP, key) ? COLUMN_FIELD_MAP[key] : (column.dataIndex || key),
    fields: COLUMN_FIELD_DEPENDENCIES[key] || [
      Object.prototype.hasOwnProperty.call(COLUMN_FIELD_MAP, key) ? COLUMN_FIELD_MAP[key] : (column.dataIndex || key),
    ].filter(Boolean),
  };
});

const isSendFailed = (status) => status !== null && status !== undefined && Number(status) === 0;

const mergeColumnConfig = (columns, existingConfig) => {
  const defaults = buildDefaultColumnConfig(columns);
  const defaultMap = new Map(defaults.map((item) => [item.key, item]));
  const normalizedConfig = Array.isArray(existingConfig) ? existingConfig : [];

  const merged = normalizedConfig
    .filter((item) => item && defaultMap.has(item.key))
    .map((item) => ({
      ...defaultMap.get(item.key),
      visible: defaultMap.get(item.key)?.locked ? true : item.visible !== false,
    }));

  defaults.forEach((item) => {
    if (!merged.some((config) => config.key === item.key)) {
      merged.push(item);
    }
  });

  return merged;
};

const moveConfigItem = (list, fromKey, toKey) => {
  const next = [...list];
  const fromIndex = next.findIndex((item) => item.key === fromKey);
  const toIndex = next.findIndex((item) => item.key === toKey);

  if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return next;

  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
};

const applySelectedFieldsToConfig = (config, selectedFields, fieldsDictionary = []) => {
  const selectedFieldSet = new Set(Array.isArray(selectedFields) ? selectedFields : []);
  const fieldsMap = new Map(
    (Array.isArray(fieldsDictionary) ? fieldsDictionary : []).map((item) => [item.field, item])
  );

  return config.map((item) => {
    const remoteField = item.field;
    const remoteMeta = remoteField ? fieldsMap.get(remoteField) : null;
    const remoteSelected = remoteMeta ? remoteMeta.selected !== false : (remoteField ? selectedFieldSet.has(remoteField) : item.visible);

    return {
      ...item,
      visible: item.locked ? true : remoteSelected,
    };
  });
};

function WorkOrder() {
  const [loading, setLoading] = useState(false);
  const [opList, setOpList] = useState([]);
  const [form] = Form.useForm();
  const [quickSearchForm] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const navigate = useNavigate();

  const [workorderData, setWorkorderData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [columnConfigDrawerOpen, setColumnConfigDrawerOpen] = useState(false);
  const [columnConfig, setColumnConfig] = useState([]);
  const [draftColumnConfig, setDraftColumnConfig] = useState([]);
  const [columnWidths, setColumnWidths] = useState({});
  const [attachmentDrawerOpen, setAttachmentDrawerOpen] = useState(false);
  const [attachmentHblNo, setAttachmentHblNo] = useState('');

  // 新增状态用于存储从API获取的筛选条件数据
  const [statusFilters, setStatusFilters] = useState([]);

  // 获取用户info
  const [userInfo, setUserInfo] = useState(getStoredUserInfo);
  const [isUserInfoLoaded, setIsUserInfoLoaded] = useState(false);
  const [queryModelId, setQueryModelId] = useState(QUERY_MODEL_SEARCH);
  const [queryModelSaving, setQueryModelSaving] = useState(false);
  const canDeleteWorkorder = Number(userInfo?.canDeleteOrder) === 1;

  const applyQueryModelState = useCallback((nextQueryModelId) => {
    if (nextQueryModelId === QUERY_MODEL_QUICK) {
      setSearchFormCollapsed(true);
      setQuickSearchCollapsed(false);
      return;
    }

    setSearchFormCollapsed(false);
    setQuickSearchCollapsed(true);
  }, []);

  useEffect(() => {
    try {
      const userinfo = getStoredUserInfo();
      if (userinfo) {
        setUserInfo(userinfo);
        const initialQueryModelId = Number(userinfo.queryModelId) === QUERY_MODEL_QUICK ? QUERY_MODEL_QUICK : QUERY_MODEL_SEARCH;
        setQueryModelId(initialQueryModelId);
        applyQueryModelState(initialQueryModelId);
        // 设置表单默认值
        // form.setFieldsValue({
        //   pic: userinfo.username
        // });
      } else {
        setQueryModelId(QUERY_MODEL_SEARCH);
        applyQueryModelState(QUERY_MODEL_SEARCH);
      }
      setIsUserInfoLoaded(true);
    } catch (error) {
      console.log('Error getting user info:', error);
      setQueryModelId(QUERY_MODEL_SEARCH);
      applyQueryModelState(QUERY_MODEL_SEARCH);
      setIsUserInfoLoaded(true);
    }
  }, [applyQueryModelState]);

  const persistQueryModelLocally = useCallback((nextQueryModelId) => {
    const nextUserInfo = {
      ...(userInfo || {}),
      queryModelId: nextQueryModelId,
    };

    setUserInfo(nextUserInfo);
    try {
      localStorage.setItem('userInfo', JSON.stringify(nextUserInfo));
    } catch (error) {
      try {
        sessionStorage.setItem('userInfo', JSON.stringify(nextUserInfo));
      } catch (sessionError) {
        console.error('Failed to persist queryModelId:', sessionError);
      }
    }
  }, [userInfo]);

  const handleQueryModelChange = useCallback(async (nextQueryModelId) => {
    if (queryModelSaving || nextQueryModelId === queryModelId) return;

    try {
      setQueryModelSaving(true);
      await authApi.updateQueryModel(nextQueryModelId);
      setQueryModelId(nextQueryModelId);
      applyQueryModelState(nextQueryModelId);
      persistQueryModelLocally(nextQueryModelId);
      messageApi.success(`Query mode switched to ${nextQueryModelId}`);
    } catch (error) {
      messageApi.error(error.response?.data?.message || error.message || 'Failed to update query mode');
    } finally {
      setQueryModelSaving(false);
    }
  }, [applyQueryModelState, messageApi, persistQueryModelLocally, queryModelId, queryModelSaving]);

  const loadColumnConfigFromStorage = useCallback(() => {
    try {
      const savedConfig = localStorage.getItem(getColumnConfigStorageKey(userInfo?.username));
      return savedConfig ? JSON.parse(savedConfig) : null;
    } catch (error) {
      console.error('Failed to parse workorder table column config:', error);
      return null;
    }
  }, [userInfo?.username]);

  const saveColumnConfigToStorage = useCallback((config) => {
    try {
      localStorage.setItem(getColumnConfigStorageKey(userInfo?.username), JSON.stringify(config));
    } catch (error) {
      console.error('Failed to save workorder table column config:', error);
    }
  }, [userInfo?.username]);

  const applyColumnWidths = useCallback((nextWidths) => {
    if (!nextWidths || typeof nextWidths !== 'object' || Array.isArray(nextWidths)) return;
    setColumnWidths(nextWidths);
  }, []);

  const saveColumnWidth = useCallback((fieldKey, width) => {
    if (!COLUMN_WIDTH_CONFIGURABLE_FIELDS.has(fieldKey)) return;
    if (columnWidthSaveTimerRef.current) clearTimeout(columnWidthSaveTimerRef.current);

    columnWidthSaveTimerRef.current = setTimeout(async () => {
      try {
        const response = await workorderApi.saveColumnWidths({
          columnWidths: { [fieldKey]: width },
        });
        applyColumnWidths(response?.data?.data?.columnWidths || {});
      } catch (error) {
        messageApi.error(error.response?.data?.message || error.message || 'Failed to save column width');
        try {
          const response = await workorderApi.getColumnWidths();
          applyColumnWidths(response?.data?.data?.columnWidths || {});
        } catch (reloadError) {
          console.error('Failed to reload column widths:', reloadError);
        }
      }
    }, 300);
  }, [applyColumnWidths, messageApi]);

  const handleColumnResize = useCallback((fieldKey, nextWidth) => {
    const width = clampColumnWidth(nextWidth);
    setColumnWidths((prev) => ({ ...prev, [fieldKey]: width }));
    saveColumnWidth(fieldKey, width);
  }, [saveColumnWidth]);

  useEffect(() => {
    if (!isUserInfoLoaded) return undefined;
    let cancelled = false;
    setColumnWidths({});

    workorderApi.getColumnWidths()
      .then((response) => {
        if (!cancelled) applyColumnWidths(response?.data?.data?.columnWidths || {});
      })
      .catch((error) => {
        if (!cancelled) console.error('Failed to load column widths:', error);
      });

    return () => {
      cancelled = true;
      if (columnWidthSaveTimerRef.current) clearTimeout(columnWidthSaveTimerRef.current);
    };
  }, [applyColumnWidths, isUserInfoLoaded, userInfo?.username]);




  // PIC 字段行内编辑
  const [picValues, setPicValues] = useState({});
  const [primeNoteValues, setPrimeNoteValues] = useState({});
  const [savingPrimeNoteSeq, setSavingPrimeNoteSeq] = useState(null);
  const [etaPortUpdateValues, setEtaPortUpdateValues] = useState({});
  const [savingEtaPortUpdateSeq, setSavingEtaPortUpdateSeq] = useState(null);
  const [etaValues, setEtaValues] = useState({});
  const [savingEtaSeq, setSavingEtaSeq] = useState(null);
  const [containerEditValues, setContainerEditValues] = useState({});
  const [savingContainerSeq, setSavingContainerSeq] = useState(null);
  const [containerModalOpen, setContainerModalOpen] = useState(false);
  const [activeContainerRecord, setActiveContainerRecord] = useState(null);
  const [activeFilter, setActiveFilter] = useState(null);
  const [exceptionCollapsed, setExceptionCollapsed] = useState(true);
  const [searchFormCollapsed, setSearchFormCollapsed] = useState(true);
  const [quickSearchCollapsed, setQuickSearchCollapsed] = useState(false);
  const [quickSearchHistory, setQuickSearchHistory] = useState(() => getStoredQuickSearchState().history);
  const [quickSearchHistoryIndex, setQuickSearchHistoryIndex] = useState(() => getStoredQuickSearchState().index);
  const [topScrollbarWidth, setTopScrollbarWidth] = useState(0);
  const tableWrapperRef = useRef(null);
  const topScrollbarRef = useRef(null);
  const syncScrollSourceRef = useRef(null);
  const draggingColumnKeyRef = useRef(null);
  const columnConfigLoadedRef = useRef(false);
  const columnWidthSaveTimerRef = useRef(null);

  const toDatePickerValue = (value) => {
    if (!value) return null;
    const d = dayjs(value, ['YYYY-MM-DD', 'MM/DD/YYYY', 'MM-DD-YYYY'], true);
    return d.isValid() ? d : null;
  };

  const toApiDate = (value) => {
    if (!value) return null;
    if (dayjs.isDayjs(value)) return value.format('YYYY-MM-DD');
    const d = dayjs(value, ['YYYY-MM-DD', 'MM/DD/YYYY', 'MM-DD-YYYY'], true);
    return d.isValid() ? d.format('YYYY-MM-DD') : null;
  };

  const hasQuickSearchHistory = quickSearchHistoryIndex >= 0 && quickSearchHistory.length > 0;

  const persistQuickSearchState = useCallback((history, index) => {
    try {
      localStorage.setItem(QUICK_SEARCH_STORAGE_KEY, JSON.stringify({ history, index }));
    } catch (error) {
      console.error('Failed to persist quick search state:', error);
    }
  }, []);

  const buildQuickSearchEntry = useCallback((values = {}, source) => {
    const nextValues = { ...(values || {}) };
    const trimmedSearchText = typeof nextValues.searchText === 'string' ? nextValues.searchText.trim() : '';
    const normalizedNoInfo = normalizeQuickSearchNoInfo(nextValues.noInfo);

    if (trimmedSearchText && !nextValues.type) {
      messageApi.warning('Type is required when Search Text is provided');
      return null;
    }

    // if (nextValues.type && !trimmedSearchText) {
    //   messageApi.warning('Search Text is required when Type is selected');
    //   return null;
    // }

    if (nextValues.type && !QUICK_SEARCH_TYPE_MAP[nextValues.type]) {
      messageApi.warning(`Unsupported quick search type: ${nextValues.type}`);
      return null;
    }

    if (nextValues.status && !QUICK_SEARCH_STATUS_MAP[nextValues.status]) {
      messageApi.warning(`Unsupported quick search status: ${nextValues.status}`);
      return null;
    }

    if (nextValues.noInfo && !normalizedNoInfo) {
      messageApi.warning(`Unsupported quick search no-info option: ${nextValues.noInfo}`);
      return null;
    }

    const request = {
      // type: nextValues.type ? QUICK_SEARCH_TYPE_MAP[nextValues.type] : undefined,
      type: (nextValues.type && trimmedSearchText) ? QUICK_SEARCH_TYPE_MAP[nextValues.type] : undefined,
      searchText: trimmedSearchText || undefined,
      fromDate: nextValues.fromDate ? dayjs(nextValues.fromDate).format('MM/DD/YYYY') : undefined,
      toDate: nextValues.toDate ? dayjs(nextValues.toDate).format('MM/DD/YYYY') : undefined,
      status: nextValues.status ? QUICK_SEARCH_STATUS_MAP[nextValues.status] : undefined,
      noInfo: normalizedNoInfo,
      search: source === 'all' ? undefined : QUICK_SEARCH_SOURCE_MAP[source],
    };

    const hasCriteria = Object.entries(request).some(([key, value]) => key !== 'search' && value !== undefined && value !== null && value !== '');
    if (!hasCriteria) {
      messageApi.warning('Please select at least one quick search condition');
      return null;
    }

    const labelParts = [];
    if (nextValues.type && trimmedSearchText) {
      labelParts.push(`${QUICK_SEARCH_TYPE_LABELS[nextValues.type]}: ${trimmedSearchText}`);
    }
    if (nextValues.status) {
      labelParts.push(`Status: ${QUICK_SEARCH_STATUS_LABELS[nextValues.status]}`);
    }
    if (nextValues.status && (nextValues.fromDate || nextValues.toDate)) {
      const fromLabel = nextValues.fromDate ? formatDisplayDate(nextValues.fromDate) : '...';
      const toLabel = nextValues.toDate ? formatDisplayDate(nextValues.toDate) : '...';
      labelParts.push(`Date: ${fromLabel} - ${toLabel}`);
    }
    if (normalizedNoInfo) {
      const noInfoLabel = QUICK_SEARCH_NO_INFO_LABELS[nextValues.noInfo] || `NO INFO ${normalizedNoInfo}`;
      labelParts.push(`No Info: ${noInfoLabel}`);
    }
    labelParts.push(`Search: ${QUICK_SEARCH_SOURCE_LABELS[source]}`);

    Object.keys(request).forEach((key) => request[key] === undefined && delete request[key]);

    const criteria = [];
    if (nextValues.type && trimmedSearchText) {
      criteria.push({
        key: `type:${nextValues.type}`,
        label: QUICK_SEARCH_TYPE_LABELS[nextValues.type],
      });
    }
    if (nextValues.status) {
      criteria.push({
        key: `status:${nextValues.status}`,
        label: QUICK_SEARCH_STATUS_LABELS[nextValues.status],
      });
    }
    if (normalizedNoInfo) {
      criteria.push({
        key: `noInfo:${normalizedNoInfo}`,
        label: QUICK_SEARCH_NO_INFO_LABELS[nextValues.noInfo] || `NO INFO ${normalizedNoInfo}`,
      });
    }
    if (source !== 'all') {
      criteria.push({
        key: `search:${source}`,
        label: `Search ${QUICK_SEARCH_SOURCE_LABELS[source]}`,
      });
    }

    return {
      id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
      label: labelParts.join(' | '),
      request,
      formValues: nextValues,
      criteria,
      source,
    };
  }, [messageApi]);

  const composeQuickSearchParams = useCallback((history, endIndex, pageNum = 1, sizeNum = pageSize) => {
    const merged = { page: pageNum, size: sizeNum };
    const assignedKeys = new Set();
    const typeList = [];
    const searchTextList = [];
    const usedTypeKeys = new Set();

    history.slice(0, endIndex + 1).forEach((entry) => {
      Object.entries(entry.request || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (key === 'search') {
            merged[key] = value;
            return;
          }
          if (key === 'type') {
            const typeKey = String(value);
            if (usedTypeKeys.has(typeKey)) return;
            usedTypeKeys.add(typeKey);
            typeList.push(value);
            searchTextList.push(entry.request?.searchText ?? '');
            return;
          }
          if (key === 'searchText') {
            return;
          }
          if (assignedKeys.has(key)) return;
          merged[key] = value;
          assignedKeys.add(key);
        }
      });
    });

    if (typeList.length > 0) {
      merged.type = typeList;
      merged.searchText = searchTextList;
    }

    return merged;
  }, [pageSize]);

  const getQuickSearchHistoryMeta = useCallback((history = [], endIndex = history.length - 1) => {
    const meta = {
      typeKeys: new Set(),
      hasStatus: false,
      hasNoInfo: false,
    };

    history.slice(0, endIndex + 1).forEach((entry) => {
      const request = entry.request || {};
      if (request.type) {
        meta.typeKeys.add(String(request.type));
      }
      if (request.status) {
        meta.hasStatus = true;
      }
      if (request.noInfo != null && request.noInfo !== '') {
        meta.hasNoInfo = true;
      }
    });

    return meta;
  }, []);

  const fetchAdvancedData = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const response = await apiClient.post('/shipping-order/query/advanced', params);
      const pageData = response?.data?.data || {};
      setWorkorderData(pageData.records || []);
      setTotal(pageData.total || 0);
      if (Object.prototype.hasOwnProperty.call(pageData, 'columnWidths')) {
        applyColumnWidths(pageData.columnWidths || {});
      }
    } catch (error) {
      setWorkorderData([]);
      setTotal(0);
      messageApi.error(error.response?.data?.message || error.message || 'Advanced search failed');
    } finally {
      setLoading(false);
    }
  }, [applyColumnWidths, messageApi]);

  const downloadExportFile = useCallback((url) => {
    if (!url) return;
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = url;
    document.body.appendChild(iframe);

    setTimeout(() => {
      if (document.body.contains(iframe)) {
        document.body.removeChild(iframe);
      }
    }, 5000);
  }, []);

  const pollExportTaskResult = useCallback(async (taskId, successMessage) => {
    const maxAttempts = 60;
    const pollInterval = 5000;
    let attempts = 0;
    let pollingTimer = null;

    const pollForResult = async () => {
      try {
        const resultResponse = await apiClient.get(`/shipping-order/export-excel/result/${taskId}`);

        if (resultResponse?.data?.code !== 200) {
          messageApi.error(`Query failed: ${resultResponse?.data?.message || 'Unknown error'}`);
          return;
        }

        const resultData = resultResponse.data;
        if (resultData.message === '导出完成') {
          if (pollingTimer) {
            clearTimeout(pollingTimer);
            pollingTimer = null;
          }

          const ossUrl = resultData.data;
          if (!ossUrl) {
            messageApi.error('Export completed but no download URL found');
            return;
          }

          downloadExportFile(ossUrl);
          messageApi.success(successMessage);
          return;
        }

        attempts += 1;
        if (attempts >= maxAttempts) {
          if (pollingTimer) {
            clearTimeout(pollingTimer);
            pollingTimer = null;
          }
          messageApi.error('Task polling timeout, please try again later');
          return;
        }

        pollingTimer = setTimeout(pollForResult, pollInterval);
      } catch (error) {
        if (pollingTimer) {
          clearTimeout(pollingTimer);
          pollingTimer = null;
        }
        messageApi.error(error.response?.data?.message || error.message || 'Failed to poll export result');
      }
    };

    pollingTimer = setTimeout(pollForResult, pollInterval);
  }, [downloadExportFile, messageApi]);

  const runQuickSearchHistory = useCallback(async (history, index, pageNum = 1, sizeNum = 20) => {
    if (index < 0 || !history[index]) return;
    const params = composeQuickSearchParams(history, index, pageNum, sizeNum);
    await fetchAdvancedData(params);
  }, [composeQuickSearchParams, fetchAdvancedData]);

  const clearQuickSearchState = useCallback((shouldResetForm = false) => {
    setQuickSearchHistory([]);
    setQuickSearchHistoryIndex(-1);
    localStorage.removeItem(QUICK_SEARCH_STORAGE_KEY);
    if (shouldResetForm) {
      quickSearchForm.resetFields();
    }
  }, [quickSearchForm]);

  const mapContainerToEditable = (c = {}) => ({
    id: c.id,
    hblNo: c.hblNo,
    containerNo: c.containerNo,
    lastFreeDay: c.lastFreeDay ?? c.last_free_day ?? null,
    pickDate: c.pickDate ?? c.pickup_date ?? null,
    returnDate: c.returnDate ?? c.return_date ?? null,
  });

  const buildQueryParams = useCallback((values = {}, pageNum = page, sizeNum = pageSize, processFilter = activeFilter) => {
    const v = { ...(values || {}) };

    const dropIfAll = (val) => {
      if (val == null) return undefined;
      const s = String(val).trim();
      return s.toLowerCase() === 'all' ? undefined : s;
    };

    const fmtDate = (d) => (d ? dayjs(d).format('YYYY-MM-DD') : undefined);

    let pic = v.pic;
    if (pic != null && String(pic).trim().toLowerCase() === 'unassigned') {
      pic = null;
    } else {
      pic = dropIfAll(pic);
    }

    let manStatus;
    if (v.manStatus != null) {
      const ms = String(v.manStatus).toLowerCase();
      if (ms === 'not_received') manStatus = 0;
      else if (ms === 'received') manStatus = 1;
    }

    let forwardEmail;
    if (v.forwardEmail != null) {
      const fe = String(v.forwardEmail).toLowerCase();
      forwardEmail = fe === 'all' ? undefined : fe;
    }

    const anDateFlag = v.anDateFlag === true ? 1 : (v.anDateFlag === false ? 0 : undefined);
    const releaseDateFlag = v.releaseDateFlag === true ? 0 : undefined;
    const zzFlag = v.zzFlag === true ? 1 : (v.zzFlag === false ? 0 : undefined);
    const noPicFlag = v.noPicFlag === true ? 1 : (v.noPicFlag === false ? 0 : undefined);
    const overdueFlag = v.overdueFlag === true ? 1 : (v.overdueFlag === false ? 0 : undefined);

    let releaseDateStart;
    let releaseDateEnd;
    if (v.releaseDateRange && v.releaseDateRange.length === 2 && v.releaseDateRange[0] && v.releaseDateRange[1]) {
      releaseDateStart = dayjs(v.releaseDateRange[0]).format('YYYY-MM-DD');
      releaseDateEnd = dayjs(v.releaseDateRange[1]).format('YYYY-MM-DD');
    }

    const params = {
      page: pageNum,
      size: sizeNum,
      hblNo: dropIfAll(v.hblNo),
      mblNo: dropIfAll(v.mblNo),
      status: dropIfAll(v.status),
      vesselName: dropIfAll(v.vesselName),
      portOfDischarge: dropIfAll(v.portOfDischarge),
      placeOfDelivery: dropIfAll(v.placeOfDelivery),
      picEmail: dropIfAll(v.picEmail),
      containerNo: dropIfAll(v.containerNo),
      voyage: dropIfAll(v.voyage),
      blType: dropIfAll(v.blType),
      agent: dropIfAll(v.agent),
      companyName: dropIfAll(v.companyName),
      primeNote: dropIfAll(v.primeNote),
      originNote: dropIfAll(v.originNote),
      pic,
      manStatus,
      anStatus: v.anStatus,
      forwardEmail,
      anDateFlag,
      releaseDateFlag,
      zzFlag,
      noPicFlag,
      overdueFlag,
      emptyEmailflag: getEmptyEmailflag(v.emptyEmailflag),
      cashierType: dropIfAll(v.cashierType),
      releaseDateStart,
      releaseDateEnd,
      etd: fmtDate(v.etd),
      eta: fmtDate(v.eta),
      sourceFlag: dropIfAll(v.sourceFlag),
    };

    Object.keys(params).forEach((k) => params[k] === undefined && delete params[k]);

    if (processFilter && processFilter.nodeNameEn) {
      params.processStatus = processFilter.nodeNameEn;
    }

    return params;
  }, [activeFilter, page, pageSize]);

  const savePrimeNote = async (record) => {
    const seq = record.seq;
    const hasDraft = Object.prototype.hasOwnProperty.call(primeNoteValues, seq);
    const value = hasDraft ? primeNoteValues[seq] : (record?.primeNote ?? '');
    try {
      setSavingPrimeNoteSeq(seq);
      const res = await apiClient.put('/shipping-order/assign-pic', {
        id: seq,
        pic: record.pic,
        primeNote: value,
      });

      if (res?.data?.code === 200) {
        setPrimeNoteValues((prev) => {
          const { [seq]: _omit, ...rest } = prev;
          return rest;
        });
        await refreshWithCurrentFilters();
        messageApi.success('Prime Note updated');
      } else {
        messageApi.error(res?.data?.message || 'Failed to update Prime Note');
      }
    } catch (error) {
      messageApi.error('Failed to update Prime Note: ' + (error.response?.data?.message || error.message));
    } finally {
      setSavingPrimeNoteSeq(null);
    }
  };

  // 保存 ETA Port Update
  const saveEtaPortUpdate = async (record) => {
    const seq = record.seq;
    const hasDraft = Object.prototype.hasOwnProperty.call(etaPortUpdateValues, seq);
    const rawValue = hasDraft ? etaPortUpdateValues[seq] : record?.eta_port_update;
    const value = toApiDate(rawValue);
    try {
      setSavingEtaPortUpdateSeq(seq);
      const res = await apiClient.put('/shipping-order/assign-pic', {
        id: seq,
        pic: record.pic,
        eta_port_update: value,
      });
      if (res?.data?.code === 200) {
        setEtaPortUpdateValues((prev) => {
          const { [seq]: _omit, ...rest } = prev;
          return rest;
        });
        await refreshWithCurrentFilters();
        messageApi.success('ETA Port Update saved');
      } else {
        messageApi.error(res?.data?.message || 'Failed to update ETA Port Update');
      }
    } catch (error) {
      messageApi.error('Failed to update ETA Port Update: ' + (error.response?.data?.message || error.message));
    } finally {
      setSavingEtaPortUpdateSeq(null);
    }
  };

  // 保存 ETA
  const saveEta = async (record) => {
    const seq = record.seq;
    const hasDraft = Object.prototype.hasOwnProperty.call(etaValues, seq);
    const rawValue = hasDraft ? etaValues[seq] : record?.eta;
    const value = toApiDate(rawValue);

    try {
      setSavingEtaSeq(seq);
      const res = await apiClient.put('/shipping-order/assign-pic', {
        id: seq,
        pic: record.pic,
        eta: value,
      });

      if (res?.data?.code === 200) {
        setEtaValues((prev) => {
          const { [seq]: _omit, ...rest } = prev;
          return rest;
        });
        await refreshWithCurrentFilters();
        messageApi.success('ETA saved');
      } else {
        messageApi.error(res?.data?.message || 'Failed to update ETA');
      }
    } catch (error) {
      messageApi.error('Failed to update ETA: ' + (error.response?.data?.message || error.message));
    } finally {
      setSavingEtaSeq(null);
    }
  };

  const openContainerModal = (record) => {
    const seq = record.seq;
    setActiveContainerRecord(record);
    setContainerModalOpen(true);
    // Always sync modal rows from the latest table record when opening.
    setContainerEditValues((prev) => ({
      ...prev,
      [seq]: (record.containers || []).map(mapContainerToEditable),
    }));
  };

  const handleContainerDateChange = (seq, containerId, field, dateValue) => {
    setContainerEditValues((prev) => ({
      ...prev,
      [seq]: (prev[seq] || []).map((item) => (
        item.id === containerId ? { ...item, [field]: toApiDate(dateValue) } : item
      )),
    }));
  };

  const saveContainerFields = async (record) => {
    const seq = record.seq;
    const currentContainers = containerEditValues[seq] || [];
    try {
      setSavingContainerSeq(seq);

      const payloadContainers = currentContainers.map((c) => ({
        id: c.id,
        hbl_no: record.hblNo,
        container_no: c.containerNo,
        last_free_day: toApiDate(c.lastFreeDay),
        pick_date: toApiDate(c.pickDate),
        return_date: toApiDate(c.returnDate),
      }));

      const res = await apiClient.put('/shipping-order/assign-pic', {
        id: seq,
        pic: record.pic,
        containers: payloadContainers,
      });

      if (res?.data?.code === 200) {
        await refreshWithCurrentFilters();
        messageApi.success('Container fields updated');
        setContainerModalOpen(false);
        setActiveContainerRecord(null);
      } else {
        messageApi.error(res?.data?.message || 'Failed to update container fields');
      }
    } catch (error) {
      messageApi.error('Failed to update container fields: ' + (error.response?.data?.message || error.message));
    } finally {
      setSavingContainerSeq(null);
    }
  };


  // 导出 shipments 和 freight charges
  const exportShipmentsandFreightCharges = async (feeFlag) => {
    try {
      const successMessage = feeFlag ? 'Freight Charges exported successfully!' : 'Shipments exported successfully!';
      let endpoint = '/shipping-order/export-excel';
      let exportParams = {};

      if (queryModelId === QUERY_MODEL_QUICK) {
        if (!hasQuickSearchHistory) {
          messageApi.warning('Please run an advanced search first');
          return;
        }

        endpoint = '/shipping-order/export-excel/advanced';
        exportParams = composeQuickSearchParams(quickSearchHistory, quickSearchHistoryIndex, page || 1, pageSize || 20);
        exportParams.feeFlag = feeFlag ?? 0;
      } else {
        const formValues = form.getFieldsValue();
        exportParams = buildQueryParams(formValues, page || 1, pageSize || 20, activeFilter);
        exportParams.feeFlag = feeFlag;
      }

      Object.keys(exportParams).forEach((key) => {
        if (exportParams[key] === undefined) {
          delete exportParams[key];
        }
      });

      const response = await apiClient.post(endpoint, exportParams);
      if (response?.data?.code !== 200) {
        messageApi.error(`Export failed: ${response?.data?.message || 'Unknown error'}`);
        return;
      }

      const taskId = response?.data?.data;
      if (!taskId) {
        messageApi.error('Export failed: No task ID returned');
        return;
      }

      messageApi.info('Export task submitted, generating file...');
      await pollExportTaskResult(taskId, successMessage);
    } catch (error) {
      console.error('Export error:', error);
      messageApi.error('Failed to export: ' + (error.response?.data?.message || error.message || 'Unknown error'));
    }
  };


  // 状态映射
  const statusMap = useMemo(() => {
    return {
      Transmitted: { color: 'purple', text: 'Transmitted' },
      LOADED: { color: 'teal', text: 'Loaded' },
      Process: { color: 'cyan', text: 'Process' },
      Update: { color: 'gold', text: 'Update' },
    };
  }, []);

  // 获取数据
  const fetchData = useCallback(async (params = {}) => {
    // 加个loading，在table
    setLoading(true);



    const { page = 1, size = 20, ...filters } = params;

    try {
      const response = await apiClient.post('/shipping-order/query', {
        page,
        size,
        ...filters,
      });

      const { records, total, columnWidths: responseColumnWidths } = response?.data?.data || {};

      setLoading(false);
      setWorkorderData(records ? records : []);
      setTotal(total);
      if (responseColumnWidths !== undefined) {
        applyColumnWidths(responseColumnWidths || {});
      }
    } catch (error) {
      setWorkorderData([]);
      setTotal(0);
      messageApi.error(error.message || '数据加载失败');
      setLoading(false);
    }
  }, [applyColumnWidths, messageApi]);

  const clearTableData = useCallback(() => {
    setWorkorderData([]);
    setTotal(0);
    setLoading(false);
  }, []);

  const refreshWithCurrentFilters = useCallback(async () => {
    if (hasQuickSearchHistory) {
      await runQuickSearchHistory(quickSearchHistory, quickSearchHistoryIndex, page, pageSize);
      return;
    }
    const formValues = form.getFieldsValue();
    const params = buildQueryParams(formValues, page, pageSize, activeFilter);
    await fetchData(params);
  }, [activeFilter, buildQueryParams, fetchData, form, hasQuickSearchHistory, page, pageSize, quickSearchHistory, quickSearchHistoryIndex, runQuickSearchHistory]);

  const readScope = getReadScope();
  const statusCardsVisible = !exceptionCollapsed;

  // 查询模式变化不重新加载字典或清空已经查询的列表。
  useEffect(() => {
    // 等待 userInfo 加载完成后再获取基础配置；工单数据只在显式 Search 后查询。
    if (!isUserInfoLoaded) return;

    clearTableData();

    // 获取op list数据
    const fetchOpListData = async () => {
      try {
        const response = await getOpInfoList();
        // console.log('opList raw data:', response.data.data);
        // API 已经返回正确的 {value, label} 格式，直接使用
        setOpList(response.data.data || []);
      } catch (error) {
        console.error('Error fetching Op List data:', error);
      }
    };

    fetchOpListData();
  }, [clearTableData, isUserInfoLoaded, readScope]);

  // 统计卡片默认折叠，用户展开后才查询。
  useEffect(() => {
    if (!isUserInfoLoaded || !statusCardsVisible) return;
    let cancelled = false;
    // 获取状态数据
    const fetchStatusData = async () => {
      try {
        const response = await apiClient.get(`/shipping-order/status-statistics`);
        // console.log(response);
        // 设置状态筛选条件数据
        if (!cancelled && response?.data?.data) {
          setStatusFilters(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching status data:', error);
      }
    };

    fetchStatusData();
    return () => { cancelled = true; };
  }, [isUserInfoLoaded, readScope, statusCardsVisible]);

  // 表单提交
  const onFinish = useCallback((values) => {
    // 下方搜索时，要取消上面 card的 active
    setActiveFilter(null);
    clearQuickSearchState(false);
    trackWorkorderSearch();

    const pageNum = 1;
    const pageSize = 20;

    const v = { ...(values || {}) };

    // 'all' 不参与查询
    const dropIfAll = (val) => {
      if (val == null) return undefined;
      const s = String(val).trim();
      return s.toLowerCase() === 'all' ? undefined : s;
    };

    // 日期格式
    const fmtDate = (d) => (d ? dayjs(d).format('YYYY-MM-DD') : undefined);

    // pic: 'Unassigned' -> null；'all' -> undefined
    let pic = v.pic;
    if (pic != null && String(pic).trim().toLowerCase() === 'unassigned') {
      pic = null;
    } else {
      pic = dropIfAll(pic);
    }

    // manStatus: not_received->0, received->1, all->undefined
    let manStatus;
    if (v.manStatus != null) {
      const ms = String(v.manStatus).toLowerCase();
      if (ms === 'not_received') manStatus = 0;
      else if (ms === 'received') manStatus = 1;
    }

    // forwardEmail: 'all' -> undefined；其他透传
    let forwardEmail;
    if (v.forwardEmail != null) {
      const fe = String(v.forwardEmail).toLowerCase();
      forwardEmail = fe === 'all' ? undefined : fe;
    }

    // 处理 flag 字段 - 转换为数字 0 或 1
    const anDateFlag = v.anDateFlag === true ? 1 : (v.anDateFlag === false ? 0 : undefined);
    const releaseDateFlag = v.releaseDateFlag === true ? 0 : undefined;
    const zzFlag = v.zzFlag === true ? 1 : (v.zzFlag === false ? 0 : undefined);
    const noPicFlag = v.noPicFlag === true ? 1 : (v.noPicFlag === false ? 0 : undefined);
    const overdueFlag = v.overdueFlag === true ? 1 : (v.overdueFlag === false ? 0 : undefined);


    // 处理 releaseDateRange -> releaseDateStart / releaseDateEnd
    let releaseDateStart;
    let releaseDateEnd;
    if (v.releaseDateRange && v.releaseDateRange.length === 2 && v.releaseDateRange[0] && v.releaseDateRange[1]) {
      releaseDateStart = dayjs(v.releaseDateRange[0]).format('YYYY-MM-DD');
      releaseDateEnd = dayjs(v.releaseDateRange[1]).format('YYYY-MM-DD');
    }

    // 组装参数
    const params = {
      page: pageNum,
      size: pageSize,

      hblNo: dropIfAll(v.hblNo),
      mblNo: dropIfAll(v.mblNo),
      status: dropIfAll(v.status),
      // processStatus: dropIfAll(v.processStatus),
      vesselName: dropIfAll(v.vesselName),
      portOfDischarge: dropIfAll(v.portOfDischarge),
      placeOfDelivery: dropIfAll(v.placeOfDelivery),
      picEmail: dropIfAll(v.picEmail),
      containerNo: dropIfAll(v.containerNo),
      voyage: dropIfAll(v.voyage),
      blType: dropIfAll(v.blType),
      agent: dropIfAll(v.agent),
      companyName: dropIfAll(v.companyName),
      primeNote: dropIfAll(v.primeNote),
      originNote: dropIfAll(v.originNote),

      pic,           // null 表示未分配
      manStatus,     // 0/1 或 undefined
      anStatus: v.anStatus,
      forwardEmail,  // 'linked' / 'not_linked' / undefined
      anDateFlag,    // 0/1 或 undefined
      releaseDateFlag, // 0/1/2 或 undefined
      zzFlag,        // 0/1 或 undefined
      noPicFlag,     // 0/1 或 undefined
      overdueFlag,   // 0/1 或 undefined
      emptyEmailflag: getEmptyEmailflag(v.emptyEmailflag),

      cashierType: dropIfAll(v.cashierType),

      releaseDateStart,  // 'YYYY-MM-DD' 或 undefined
      releaseDateEnd,    // 'YYYY-MM-DD' 或 undefined


      etd: fmtDate(v.etd),
      eta: fmtDate(v.eta),
      sourceFlag: dropIfAll(v.sourceFlag),
    };

    // 去掉 undefined 字段
    Object.keys(params).forEach((k) => params[k] === undefined && delete params[k]);

    fetchData(params);
    setPage(pageNum);
    setPageSize(pageSize);
  }, [clearQuickSearchState, fetchData]);




  // 重置表单
  const onReset = useCallback(() => {
    form.resetFields();
    clearQuickSearchState(true);
    setActiveFilter(null);
    clearTableData();
    setPage(1);
    setPageSize(20);
  }, [clearQuickSearchState, clearTableData, form]);

  // 快速搜索 (Quick Search)
  const handleQuickSearch = useCallback(async (source) => {
    const values = quickSearchForm.getFieldsValue();
    const entry = buildQuickSearchEntry(values, source);
    if (!entry) return;

    setActiveFilter(null);

    const baseHistory = quickSearchHistory.slice(0, quickSearchHistoryIndex + 1);
    const historyMeta = getQuickSearchHistoryMeta(baseHistory, baseHistory.length - 1);
    const nextRequest = entry.request || {};

    if (nextRequest.type && historyMeta.typeKeys.has(String(nextRequest.type))) {
      quickSearchForm.resetFields();
      return;
    }

    if (nextRequest.status && historyMeta.hasStatus) {
      quickSearchForm.resetFields();
      return;
    }

    if (nextRequest.noInfo != null && nextRequest.noInfo !== '' && historyMeta.hasNoInfo) {
      quickSearchForm.resetFields();
      return;
    }

    const nextHistory = [...baseHistory, entry];
    const nextIndex = nextHistory.length - 1;
    const nextPage = 1;
    const nextPageSize = 20;

    trackWorkorderSearch();
    setQuickSearchHistory(nextHistory);
    setQuickSearchHistoryIndex(nextIndex);
    persistQuickSearchState(nextHistory, nextIndex);
    setPage(nextPage);
    setPageSize(nextPageSize);
    quickSearchForm.resetFields();

    await runQuickSearchHistory(nextHistory, nextIndex, nextPage, nextPageSize);
  }, [buildQuickSearchEntry, getQuickSearchHistoryMeta, persistQuickSearchState, quickSearchForm, quickSearchHistory, quickSearchHistoryIndex, runQuickSearchHistory]);

  const handleQuickSearchHistoryStep = useCallback(async (direction) => {
    if (!hasQuickSearchHistory) return;

    const nextIndex = quickSearchHistoryIndex + direction;
    if (nextIndex < 0 || nextIndex >= quickSearchHistory.length) return;

    const truncatedHistory = quickSearchHistory.slice(0, nextIndex + 1);
    const currentEntry = quickSearchHistory[quickSearchHistoryIndex];

    setQuickSearchHistory(truncatedHistory);
    setQuickSearchHistoryIndex(nextIndex);
    persistQuickSearchState(truncatedHistory, nextIndex);
    setPage(1);
    setPageSize(20);
    quickSearchForm.resetFields();
    if (direction < 0 && currentEntry?.formValues) {
      quickSearchForm.setFieldsValue(currentEntry.formValues);
    }

    await runQuickSearchHistory(truncatedHistory, nextIndex, 1, 20);
  }, [hasQuickSearchHistory, persistQuickSearchState, quickSearchForm, quickSearchHistory, quickSearchHistoryIndex, runQuickSearchHistory]);

  const handleQuickSearchReset = useCallback(() => {
    clearQuickSearchState(true);
    setActiveFilter(null);
    clearTableData();
    setPage(1);
    setPageSize(20);
  }, [clearQuickSearchState, clearTableData]);

  // Mode 2 uses page-level shortcuts so they also work after a control loses focus.
  useEffect(() => {
    if (quickSearchCollapsed) return undefined;

    const handleQuickSearchShortcut = (event) => {
      if (event.repeat || event.isComposing || event.nativeEvent?.isComposing) {
        return;
      }

      if (event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
        handleQuickSearch('all');
        return;
      }

      const key = String(event.key || '').toLowerCase();
      if (event.shiftKey && key === 'r') {
        event.preventDefault();
        event.stopPropagation();
        handleQuickSearchReset();
        return;
      }

      if (event.shiftKey && key === 't') {
        event.preventDefault();
        event.stopPropagation();
        handleQuickSearchHistoryStep(-1);
      }
    };

    window.addEventListener('keydown', handleQuickSearchShortcut, true);
    return () => window.removeEventListener('keydown', handleQuickSearchShortcut, true);
  }, [handleQuickSearch, handleQuickSearchHistoryStep, handleQuickSearchReset, quickSearchCollapsed]);



  // 顶部复制/删除工单
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [copyActionType, setCopyActionType] = useState('copy');
  const [copySearchHblNo, setCopySearchHblNo] = useState('');
  const [copyHblNo, setCopyHblNo] = useState('');
  const [copyLoading, setCopyLoading] = useState(false);
  const [copyDetailLoading, setCopyDetailLoading] = useState(false);
  const [copyDetailData, setCopyDetailData] = useState(null);

  const resetCopyModalState = () => {
    setCopyModalOpen(false);
    setCopyActionType('copy');
    setCopySearchHblNo('');
    setCopyHblNo('');
    setCopyDetailData(null);
    setCopyDetailLoading(false);
  };

  const handleOpenCopyModal = (actionType = 'copy') => {
    setCopyActionType(actionType);
    setCopyModalOpen(true);
    setCopySearchHblNo('');
    setCopyHblNo('');
    setCopyDetailData(null);
  };

  const handleSearchCopyTarget = async () => {
    const targetHblNo = copySearchHblNo.trim();
    if (!targetHblNo) {
      messageApi.warning('Please enter HBL No.');
      return;
    }

    setCopyDetailData(null);
    setCopyHblNo('');
    setCopyDetailLoading(true);
    trackWorkorderSearch();
    try {
      const response = await apiClient.get(`/shipping-order/detail/${targetHblNo}`);
      const detailData = response?.data?.data || {};
      if (!detailData || Object.keys(detailData).length === 0) {
        messageApi.warning('No workorder found for the entered HBL No.');
        return;
      }
      setCopyDetailData(detailData);
      if (copyActionType === 'copy') {
        setCopyHblNo('');
      }
    } catch (error) {
      console.error(`Failed to fetch detail for ${copyActionType}:`, error);
      messageApi.error('Failed to load workorder detail');
    } finally {
      setCopyDetailLoading(false);
    }
  };

  const handleCopyConfirm = async () => {
    if (!copyHblNo || !copyHblNo.trim()) {
      messageApi.warning('Please enter HBL No.');
      return;
    }
    if (!copyDetailData) {
      messageApi.error('Detail data not loaded');
      return;
    }
    setCopyLoading(true);
    try {
      // 从详情数据中提取 containers 并处理日期格式
      const containers = (copyDetailData.containers || []).map(c => {
        const formatted = { ...c };
        // 移除 id，让后端生成新的
        delete formatted.id;
        delete formatted.seq;
        // 格式化日期字段
        ['pickup_date', 'return_date', 'last_free_day'].forEach(dateField => {
          if (formatted[dateField] && typeof formatted[dateField] === 'string') {
            const d = dayjs(formatted[dateField]);
            if (d.isValid()) {
              formatted[dateField] = d.format('YYYY-MM-DD');
            }
          }
        });
        return formatted;
      });

      // 准备表单数据，排除 containers 和一些不需要复制的字段
      const {
        containers: _c,
        id,
        seq,
        created_at,
        created_by,
        updated_at,
        hbl_receipt_date,
        payment_receipt_date,
        release_date,
        release_flag,
        cashier_type,
        pickup_issuing_date,
        ...formData
      } = copyDetailData;

      // 格式化日期字段
      const dateFields = [
        'etd', 'eta_port', 'customs_entry_date', 'customs_exam_date',
        'customs_release_date', 'go_date', 'pickup_issuing_date',
        'eta_port_update', 'dest_eta', 'an_date', 'it_date',
        'confirmation_date', 'advice_date', 'hbl_receipt_date',
        'payment_receipt_date'
      ];
      dateFields.forEach(dateField => {
        if (formData[dateField] && typeof formData[dateField] === 'string') {
          const d = dayjs(formData[dateField]);
          if (d.isValid()) {
            formData[dateField] = d.format('YYYY-MM-DD');
          }
        }
      });

      const requestData = {
        ...formData,
        hbl_no: copyHblNo.trim(),
        containers,
      };

      const response = await apiClient.post('/shipping-order/create', requestData);
      if (response.data.code === 200) {
        messageApi.success('Copy & Create Success');
        resetCopyModalState();
        // 刷新列表
        refreshWithCurrentFilters();
      } else {
        messageApi.error(response.data.message || 'Copy Create Failure');
      }
    } catch (error) {
      console.error('Copy Create Failure', error);
      messageApi.error('Copy Create Failure: ' + (error.response?.data?.message || error.message));
    } finally {
      setCopyLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!copyDetailData) {
      messageApi.warning('Please search a valid workorder first');
      return;
    }

    const deleteTargetId = copyDetailData?.seq ?? copyDetailData?.id;
    if (!deleteTargetId) {
      messageApi.error('Delete target id is missing');
      return;
    }

    setCopyLoading(true);
    try {
      const response = await apiClient.delete(`/shipping-order/${deleteTargetId}`);
      if (response.data.code === 200) {
        messageApi.success('Delete Success');
        resetCopyModalState();
        // 删除后回到未查询的初始状态，避免用旧筛选条件再次请求列表。
        form.resetFields();
        clearQuickSearchState(true);
        setActiveFilter(null);
        clearTableData();
        setPage(1);
        setPageSize(20);
      } else {
        messageApi.error(response.data.message || 'Delete Failure');
      }
    } catch (error) {
      console.error('Delete Failure', error);
      messageApi.error('Delete Failure: ' + (error.response?.data?.message || error.message));
    } finally {
      setCopyLoading(false);
    }
  };


  // 标记为已读
  const markAsRead = async (itemId) => {
    try {
      // 乐观更新：立即更新本地状态
      setWorkorderData(prevData =>
        prevData.map(order => ({
          ...order,
          notifications: {
            ...order.notifications,
            messages: order.notifications.messages.map(msg =>
              msg.id === itemId ? { ...msg, readStatus: 1 } : msg
            )
          }
        }))
      );

      await apiClient.post(`/message-notifications/${itemId}/read`);
      messageApi.success('Marked as read');
      // // 保持当前分页和筛选条件
      // const formValues = form.getFieldsValue();
      // const anDateFlag = formValues.anDateFlag === true ? 1 : (formValues.anDateFlag === false ? 0 : undefined);
      // const releaseDateFlag = formValues.releaseDateFlag === true ? 1 : (formValues.releaseDateFlag === false ? 0 : undefined);
      // const zzFlag = formValues.zzFlag === true ? 1 : (formValues.zzFlag === false ? 0 : undefined);
      // const noPicFlag = formValues.noPicFlag === true ? 1 : (formValues.noPicFlag === false ? 0 : undefined);

      // const extraParams = {};
      // if (activeFilter && activeFilter.nodeNameEn) {
      //   extraParams.processStatus = activeFilter.nodeNameEn;
      // }

      // fetchData({ 
      //   page: page, 
      //   size: pageSize, 
      //   ...formValues, 
      //   anDateFlag,
      //   releaseDateFlag,
      //   zzFlag,
      //   noPicFlag,
      //   ...extraParams 
      // });
    } catch (error) {
      messageApi.error('Failed to mark as read: ' + error.message);
    }
  };


  // 标记为已解决
  const markAsResolved = async (itemId) => {
    try {
      // 乐观更新：立即更新本地状态
      setWorkorderData(prevData =>
        prevData.map(order => ({
          ...order,
          notifications: {
            ...order.notifications,
            messages: order.notifications.messages.map(msg =>
              msg.id === itemId ? { ...msg, solveStatus: 1 } : msg
            )
          }
        }))
      );

      await apiClient.post(`/message-notifications/${itemId}/solve`);
      messageApi.success('Marked as resolved');
    } catch (error) {
      messageApi.error('Failed to resolve: ' + error.message);
    }
  };

  const openAttachmentDrawer = useCallback((record) => {
    setAttachmentHblNo(record?.hblNo || '');
    setAttachmentDrawerOpen(true);
  }, []);

  const handleCopyPicLabel = useCallback(async (picValue) => {
    const option = opList.find((item) => String(item?.value) === String(picValue));
    const picLabel = option?.label || picValue;

    if (!picLabel) {
      messageApi.warning('Please select a PIC first');
      return;
    }

    try {
      await navigator.clipboard.writeText(String(picLabel));
      messageApi.success('PIC copied');
    } catch (error) {
      const textarea = document.createElement('textarea');
      textarea.value = String(picLabel);
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      const copied = document.execCommand('copy');
      document.body.removeChild(textarea);
      if (copied) messageApi.success('PIC copied');
      else messageApi.error('Failed to copy PIC');
    }
  }, [messageApi, opList]);

  const hblColumnWidth = useMemo(() => {
    const minimumWidth = 120;
    const maximumWidth = 260;

    return workorderData.reduce((widest, record) => {
      const hasNoFld = Array.isArray(record.containers)
        && record.containers.some((container) => Number(container?.lackLastFreeDayFlag) === 1);
      const indicatorWidths = [
        isSendFailed(record.anSendStatus) ? 32 : 0,
        isSendFailed(record.puSendStatus) ? 32 : 0,
        hasNoFld ? 45 : 0,
        record.blacklistFlag === 1 ? 58 : 0,
      ].filter(Boolean);
      const hblTextWidth = Math.max(72, String(record.hblNo || '').length * 7 + 12);
      const requiredWidth = hblTextWidth
        + indicatorWidths.reduce((total, width) => total + width, 0)
        + (indicatorWidths.length * 4)
        + 12;

      return Math.max(widest, Math.min(maximumWidth, requiredWidth));
    }, minimumWidth);
  }, [workorderData]);



  // 表格列定义
  const baseColumns = useMemo(() => [
    // {
    //   title: 'Seq',
    //   dataIndex: 'seq',
    //   key: 'seq',
    //   width: 80,
    //   align: 'center',
    //   sorter: (a, b) => a.seq - b.seq,
    //   fixed: 'left',
    // },
    // {
    //   title: 'autoSerialNo',
    //   dataIndex: 'autoSerialNo',
    //   key: 'autoSerialNo',
    //   width: 80,
    //   align: 'center',
    //   fixed: 'left',
    // },
    {
      title: 'No.',
      key: 'rowNo',
      className: 'row-no-col',
      width: 40,
      align: 'center',
      fixed: 'left',
      render: (_, __, index) => (page - 1) * pageSize + index + 1,
    },
    {
      title: 'HBL',
      dataIndex: 'hblNo',
      key: 'hblNo',
      width: hblColumnWidth,
      fixed: 'left',
      render: (text, record) => {
        const hasNoFld = Array.isArray(record.containers)
          && record.containers.some((c) => Number(c?.lackLastFreeDayFlag) === 1);
        const anSendFailed = isSendFailed(record.anSendStatus);
        const puSendFailed = isSendFailed(record.puSendStatus);

        return (
          <span className="workorder-hbl-cell">
            <span>{text}</span>


            {anSendFailed && (
              <Tooltip title="A/N send failed">
                <Tag className="workorder-send-failed-tag" color="error" icon={<MailOutlined />}></Tag>
              </Tooltip>
            )}
            {puSendFailed && (
              <Tooltip title="P/U send failed">
                <Tag className="workorder-send-failed-tag" color="error" icon={<MailOutlined />}></Tag>
              </Tooltip>
            )}
            
            {hasNoFld && <Tag color="warning">NOLFD</Tag>}
            {record.blacklistFlag === 1 && (
              <h4 style={{ color: '#ff4d4f', fontSize: '8px' }}>In Black List</h4>
            )}
          </span>
        );
      },
    },
    {
      title: 'MBL',
      dataIndex: 'mblNo',
      key: 'mblNo',
      width: 150,
    },
    {
      title: 'Consignee Company',
      dataIndex: 'consigneeCompany',
      key: 'consigneeCompany',
      width: 200,
      render: (text) => text || '-',
    },
    {
      title: 'PIC',
      dataIndex: 'pic',
      key: 'pic',
      width: 160,
      render: (text, record) => {
        const currentPic = picValues[record.seq] !== undefined ? picValues[record.seq] : (text || undefined);

        return (
        <Space size={2} className="workorder-pic-editor">
          {/* <UserOutlined /> */}
          <Select
            className="workorder-pic-select"
            value={currentPic}
            onChange={async (value) => {
              // 乐观更新：先更新本地状态和表格数据
              const originalPic = text; // 保存原始值，用于回滚
              const nextPic = value ?? '';
              setPicValues(prev => ({ ...prev, [record.seq]: nextPic }));
              setWorkorderData(prevData =>
                prevData.map(order =>
                  order.seq === record.seq ? { ...order, pic: nextPic } : order
                )
              );

              try {
                const response = await apiClient.put('/shipping-order/assign-pic', {
                  id: record.seq,
                  pic: nextPic
                });

                if (response.data.code === 200) {
                  setPicValues(prev => {
                    const { [record.seq]: _omit, ...rest } = prev;
                    return rest;
                  });
                  await refreshWithCurrentFilters();
                  messageApi.success('PIC updated successfully');
                } else {
                  messageApi.error(`Update failed: ${response.data.message || 'Unknown error'}`);
                  // 失败：回滚状态
                  setPicValues(prev => ({ ...prev, [record.seq]: originalPic }));
                  setWorkorderData(prevData =>
                    prevData.map(order =>
                      order.seq === record.seq ? { ...order, pic: originalPic } : order
                    )
                  );
                }
              } catch (error) {
                console.error('Update PIC error:', error);
                messageApi.error('Failed to update PIC: ' + (error.response?.data?.message || error.message));
                // 失败：回滚状态
                setPicValues(prev => ({ ...prev, [record.seq]: originalPic }));
                setWorkorderData(prevData =>
                  prevData.map(order =>
                    order.seq === record.seq ? { ...order, pic: originalPic } : order
                  )
                );
              }
            }}
            size="small"
            allowClear
            showSearch
            placeholder="Select PIC"
            options={opList}
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
          <Tooltip title="Copy PIC">
            <Button
              className="workorder-pic-copy-button"
              type="text"
              size="small"
              icon={<CopyOutlined />}
              disabled={!currentPic}
              onClick={(event) => {
                event.stopPropagation();
                handleCopyPicLabel(currentPic);
              }}
            />
          </Tooltip>
        </Space>
        );
      },
    },
    {
      title: 'Container No.',
      dataIndex: 'containerNumbers',
      key: 'containerNumbers',
      width: 180,
      render: (text, record) => {
        if (!Array.isArray(text) || text.length === 0) return '-'; // 如果 text 不是数组或为空数组，返回 '-'
        const [firstContainer, ...restContainers] = text;
        return (
          <div className='container-cell-wrap'>
            <Space size={[4, 4]} wrap>
              <Tag color='blue'>{firstContainer}</Tag>
              {restContainers.length > 0 && (
                <Popover
                  trigger="hover"
                  content={
                    <Space size={[4, 4]} wrap>
                      {text.map((item, index) => (
                        <Tag color='blue' key={index}>{item}</Tag>
                      ))}
                    </Space>
                  }
                >
                  <Tag color="default" style={{ cursor: 'pointer' }}>
                    +{restContainers.length}
                  </Tag>
                </Popover>
              )}
            </Space>
            <Button
              size="small"
              className='container-expand-btn'
              icon={<SecurityScanOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                openContainerModal(record);
              }}
            />
          </div>
        );
      },
    },




    {
      title: 'Port of Discharge',
      dataIndex: 'portOfDischarge',
      key: 'portOfDischarge',
      width: 150,
    },

    {
      title: 'Place of Delivery',
      dataIndex: 'placeOfDelivery',
      key: 'placeOfDelivery',
      width: 150,
    },
    

    {
      title: 'Prime Note',
      dataIndex: 'primeNote',
      key: 'primeNote',
      className: 'prime-note-col',
      width: 240,
      render: (text, record) => (
        <Space.Compact style={{ width: '100%', alignItems: 'center' }}>
          <Input
            size="small"
            value={primeNoteValues[record.seq] !== undefined ? primeNoteValues[record.seq] : (text || '')}
            onChange={(e) => {
              const next = e.target.value;
              setPrimeNoteValues((prev) => ({ ...prev, [record.seq]: next }));
            }}
            placeholder="Prime Note"
          />
          <Button
            type="primary"
            size="small"
            loading={savingPrimeNoteSeq === record.seq}
            onClick={() => savePrimeNote(record)}
            icon={<SaveOutlined />}
          >
          </Button>
        </Space.Compact>
      ),
    },
    {
      title: 'Origin Note',
      dataIndex: 'origin_note',
      key: 'origin_note',
      width: 240,
      render: (text) => text || '-',
    },
    {
      title: 'ETA',
      dataIndex: 'eta',
      key: 'eta',
      className: 'eta-col',
      width: 180,
      render: (text, record) => (
        <Space.Compact style={{ width: '100%', alignItems: 'center' }}>
          <DatePicker
            size="small"
            format="YYYY-MM-DD"
            value={
              etaValues[record.seq] !== undefined
                ? toDatePickerValue(etaValues[record.seq])
                : toDatePickerValue(text)
            }
            onChange={(date) => {
              setEtaValues((prev) => ({ ...prev, [record.seq]: date }));
            }}
            style={{ width: '100%' }}
          />
          <Button
            type="primary"
            size="small"
            loading={savingEtaSeq === record.seq}
            onClick={() => saveEta(record)}
            icon={<SaveOutlined />}
          />
        </Space.Compact>
      ),
    },
    {
      title: 'ETA Port Update',
      dataIndex: 'eta_port_update',
      key: 'eta_port_update',
      className: 'eta-port-update-col',
      width: 180,
      render: (text) => formatDisplayDate(text),
      // render: (text, record) => (
      //   <Space.Compact style={{ width: '100%', alignItems: 'center' }}>
      //     <DatePicker
      //       size="small"
      //       format="YYYY-MM-DD"
      //       value={
      //         etaPortUpdateValues[record.seq] !== undefined
      //           ? toDatePickerValue(etaPortUpdateValues[record.seq])
      //           : toDatePickerValue(text)
      //       }
      //       onChange={(date) => {
      //         setEtaPortUpdateValues((prev) => ({ ...prev, [record.seq]: date }));
      //       }}
      //       style={{ width: '100%' }}
      //     />
      //     <Button
      //       type="primary"
      //       size="small"
      //       loading={savingEtaPortUpdateSeq === record.seq}
      //       onClick={() => saveEtaPortUpdate(record)}
      //       icon={<SaveOutlined />}
      //     />
      //   </Space.Compact>
      // ),
    },



    {
      title: 'VESSEL',
      dataIndex: 'vesselName',
      key: 'vesselName',
      width: 120,
    },
    {
      title: 'VOY',
      dataIndex: 'voyage',
      key: 'voyage',
      width: 120,
    },
    {
      title: 'ETD',
      dataIndex: 'etd',
      key: 'etd',
      width: 150,
      render: (text) => formatDisplayDate(text),
    },

    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => {
        const statusInfo = statusMap[status] || { color: 'default', text: status };
        return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
      },
      filters: Object.keys(statusMap).map((key) => ({
        text: statusMap[key].text,
        value: key,
      })),
      onFilter: (value, record) => record.status === value,
    },
    {
      title: 'Source Flag',
      dataIndex: 'sourceFlag',
      key: 'sourceFlag',
      width: 120,
      render: (text) => {
        if (!text) return '-';
        const colorMap = {
          pms: 'blue',
          pai: 'green',
          primeone: 'purple',
        };
        return <Tag color={colorMap[text.toLowerCase()] || 'default'}>{String(text).toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Transmitted',
      dataIndex: 'transmittedTime',
      key: 'transmittedTime',
      width: 130,
      render: (text, record) => {
          if (!text) return '-';
        
          if (dayjs.isDayjs(text)) {
            return text.format('MM/DD/YY HH:MM:ss');
          }
        
          const parsed = dayjs(text, 'YYYY-MM-DD HH:MM:ss', true);
          if (parsed.isValid()) {
            return parsed.format('MM/DD/YY HH:MM:ss');
          }
        
          const fallback = dayjs(text);
          return fallback.isValid() ? fallback.format('MM/DD/YY HH:MM:ss') : text;
      }
  
    },
    
    {
      title: 'Email Count',
      dataIndex: 'forwardingRecord',
      key: 'forwardingRecord',
      width: 120,
      render: (text, record) => {
        if (record.forwardingRecord === '0') {
          return <span>{record.forwardingRecord}</span>;
        } else {
          return (
            <a
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                e.preventDefault();
                navigate(`/email?workorderId=${record.seq}`);
              }}
            >
              {record.forwardingRecord}
            </a>
          );
        }
      },
    },
    {
      title: 'P/U',
      dataIndex: 'pickupNumberStatus',
      key: 'pickupNumberStatus',
      width: 100,
      render: (status) => {
        // 0: 未处理, 1: 已处理
        const statusMap = {
          0: { color: 'orange', text: 'Pending' },
          1: { color: 'green', text: 'Processed' }
        };
        const statusInfo = statusMap[status] || { color: 'default', text: status };
        return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
      },
    },
    {
      title: 'Free Storage Days',
      dataIndex: 'freeStorageDays',
      key: 'freeStorageDays',
      width: 160,
      render: (days) => days || '-',
    },
    {
      title: 'Free Container Days',
      dataIndex: 'freeContainerDays',
      key: 'freeContainerDays',
      width: 160,
      render: (days) => days || '-',
    },
    {
      title: 'Overdue Pickup Days',
      dataIndex: 'overduePickupDays',
      key: 'overduePickupDays',
      width: 160,
      render: (days) => days || '-',
    },
    {
      title: 'Overdue Return Days',
      dataIndex: 'overdueReturnDays',
      key: 'overdueReturnDays',
      width: 160,
      render: (days) => days || '-',
    },
    {
      title: 'BL Type',
      dataIndex: 'blType',
      key: 'blType',
      width: 120,
      render: (type) => type || '-',
    },
    {
      title: 'Cashier',
      dataIndex: 'cashierType',
      key: 'cashierType',
      width: 120,
      render: (type) => type || '-',
    },
    {
      title: 'Pending Fees',
      dataIndex: 'pendingFees',
      key: 'pendingFees',
      width: 120,
      render: (fees) => {
        // 显示费用信息，如果为null或0则显示'-'
        if (fees == null || fees === 0) {
          return '-';
        }
        return `$${fees.toFixed(2)}`;
      },
    },
    {
      title: 'A/N Status',
      dataIndex: 'anStatus',
      key: 'anStatus',
      width: 150,
      render: (status) => {
        // 0: 未发送, 1: 已发送
        const statusMap = {
          0: { color: 'orange', text: 'Unsent' },
          1: { color: 'green', text: 'Sent' }
        };
        const statusInfo = statusMap[status] || { color: 'default', text: status };
        return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
      },
    },
    {
      title: 'Documents',
      dataIndex: 'documentPaths',
      key: 'documentPaths',
      width: 120,
      render: (documents) => {
        // 检查 documents 是否为数组且不为空
        if (!Array.isArray(documents) || documents.length === 0) {
          return '-';
        }

        return (
          <Space size={4}>
            {documents.map((doc, index) => (
              <Tooltip key={index} title={doc.filename}>
                <FileProtectOutlined />
              </Tooltip>
            ))}
          </Space>
        );
      },
    },
    {
      title: 'Action',
      key: 'action',
      className: 'workorder-action-cell',
      render: (_, record) => (
        <Space size="middle">
          {/* {record.sourceFlag !== 'pms' && ( */}
            <Button
              type="primary"
              icon={<InfoCircleOutlined />}
              size="middle"
              style={{
                width: 50,
                color: '#fff'
              }}
              color='#cacaca'
              onClick={(e) => {
                e.stopPropagation();
                window.open(`/workorderdetail/${record.hblNo}`, '_blank');
              }}
            />
          {/* )} */}

          

          <Popover
            content={
              <div style={{ maxWidth: 400, maxHeight: 400, overflowY: 'auto' }}>
                {record.notifications.messages.map((item, index) => (
                  <div key={item.id || index} style={{
                    padding: '12px',
                    borderBottom: index < record.notifications.messages.length - 1 ? '1px solid #f0f0f0' : 'none',
                    marginBottom: '8px',
                    fontSize: 12,
                    borderLeft: item.readStatus === 1 ? '5px solid #52c41a' : '5px solid #1f63fb',
                    opacity: item.solveStatus === 1 ? 0.6 : 1,
                    filter: item.solveStatus === 1 ? 'grayscale(0.6)' : 'none'
                  }}>
                    <div style={{
                      fontWeight: 'bold',
                      marginBottom: '8px',
                      textDecoration: item.solveStatus === 1 ? 'line-through' : 'none',
                    }}>
                      {item.messageContent}
                    </div>
                    <div>MBL: {item.mblNo}</div>
                    <div style={{ color: '#1f63fb' }}>HBL: {item.hblNo}</div>
                    <div>Time: {new Date(item.createTime).toLocaleString('zh-CN', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit'
                    }).replace(/\//g, '/').replace(',', '')}</div>

                    <div style={{ marginTop: '8px', textAlign: 'right' }}>
                      <Button
                        type="link"
                        size="small"
                        icon={<CheckOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          markAsRead(item.id);
                        }}
                        disabled={item.readStatus === 1} // 如果已经是已读状态，则禁用
                      >
                        Read
                      </Button>
                      <Button
                        type="link"
                        size="small"
                        icon={<CheckCircleFilled />}
                        onClick={(e) => {
                          e.stopPropagation();
                          markAsResolved(item.id);
                        }}
                        disabled={item.solveStatus === 1} // 如果已经是已解决状态，则禁用
                      >
                        Resolve
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            }
            title="Notifications"
            trigger="click"
            placement="bottomRight"
          >
            <Badge
              className="workorder-notification-badge"
              count={record.notifications.messages.filter(msg => msg.readStatus !== 1 && msg.solveStatus !== 1).length}
              overflowCount={99}
            >
              <Button
                type="default"
                icon={<BellOutlined />}
                size="small"
              />
            </Badge>
          </Popover>


        </Space>
      ),
      width: 110,
      fixed: 'right',
    },
    {
      title: 'Files',
      key: 'attachments',
      width: 40,
      fixed: 'right',
      render: (_, record) => (
        <Button
          type="default"
          size="small"
          icon={<PaperClipOutlined />}
          onClick={(event) => {
            event.stopPropagation();
            openAttachmentDrawer(record);
          }}
        >
        </Button>
      ),
    },
  ], [
    navigate,
    statusMap,
    userInfo,
    opList,
    picValues,
    primeNoteValues,
    savingPrimeNoteSeq,
    etaPortUpdateValues,
    savingEtaPortUpdateSeq,
    etaValues,
    savingEtaSeq,
    containerEditValues,
    savingContainerSeq,
    hblColumnWidth,
    handleCopyPicLabel,
    refreshWithCurrentFilters,
    openAttachmentDrawer,
  ]);

  useEffect(() => {
    if (!isUserInfoLoaded) return;
    columnConfigLoadedRef.current = false;
    setColumnConfig([]);
    setDraftColumnConfig([]);
  }, [isUserInfoLoaded, userInfo?.username]);

  useEffect(() => {
    if (!isUserInfoLoaded || columnConfigLoadedRef.current) return;

    let cancelled = false;

    const loadColumnConfig = async () => {
      const storageConfig = loadColumnConfigFromStorage();

      try {
        const response = await workorderApi.getDisplayFieldsConfig();
        if (cancelled) return;

        const remoteData = response?.data?.data || {};
        const mergedConfig = mergeColumnConfig(baseColumns, storageConfig);
        const nextConfig = applySelectedFieldsToConfig(
          mergedConfig,
          remoteData.selectedFields,
          remoteData.fields
        );

        setColumnConfig(nextConfig);
        saveColumnConfigToStorage(nextConfig);
      } catch (error) {
        if (cancelled) return;
        setColumnConfig(mergeColumnConfig(baseColumns, storageConfig));
      } finally {
        if (!cancelled) {
          columnConfigLoadedRef.current = true;
        }
      }
    };

    loadColumnConfig();

    return () => {
      cancelled = true;
    };
  }, [baseColumns, isUserInfoLoaded, loadColumnConfigFromStorage, saveColumnConfigToStorage]);

  useEffect(() => {
    if (!columnConfigLoadedRef.current) return;
    setColumnConfig((prev) => mergeColumnConfig(baseColumns, prev));
  }, [baseColumns]);

  const columns = useMemo(() => {
    const columnMap = new Map(
      baseColumns.map((column, index) => [column.key || column.dataIndex || `column-${index}`, column])
    );
    const visibleColumns = columnConfig.length
      ? columnConfig
        .filter((item) => item.visible)
        .map((item) => columnMap.get(item.key))
        .filter(Boolean)
      : baseColumns;

    return visibleColumns.map((column) => {
      const fieldKey = typeof column.dataIndex === 'string' ? column.dataIndex : null;
      const isResizable = fieldKey && COLUMN_WIDTH_CONFIGURABLE_FIELDS.has(fieldKey);
      const width = isResizable && Object.prototype.hasOwnProperty.call(columnWidths, fieldKey)
        ? clampColumnWidth(columnWidths[fieldKey])
        : column.width;

      return {
        ...column,
        width,
        onHeaderCell: () => ({
          width,
          onResize: isResizable ? (nextWidth) => handleColumnResize(fieldKey, nextWidth) : undefined,
        }),
      };
    });
  }, [baseColumns, columnConfig, columnWidths, handleColumnResize]);

  const openColumnConfigDrawer = useCallback(() => {
    const mergedConfig = mergeColumnConfig(baseColumns, columnConfig);
    setDraftColumnConfig(mergedConfig);
    setColumnConfigDrawerOpen(true);
  }, [baseColumns, columnConfig]);

  const handleColumnVisibilityChange = useCallback((key, checked) => {
    setDraftColumnConfig((prev) => prev.map((item) => (
      item.key === key && !item.locked ? { ...item, visible: checked } : item
    )));
  }, []);

  const handleColumnDragStart = useCallback((key) => {
    if (LOCKED_COLUMN_KEYS.has(key)) return;
    draggingColumnKeyRef.current = key;
  }, []);

  const handleColumnDrop = useCallback((targetKey) => {
    const sourceKey = draggingColumnKeyRef.current;
    if (!sourceKey || sourceKey === targetKey || LOCKED_COLUMN_KEYS.has(targetKey)) return;
    setDraftColumnConfig((prev) => moveConfigItem(prev, sourceKey, targetKey));
    draggingColumnKeyRef.current = null;
  }, []);

  const handleColumnConfigSave = useCallback(() => {
    if (!draftColumnConfig.some((item) => item.visible)) {
      messageApi.warning('Keep at least one visible column');
      return;
    }

    const nextConfig = mergeColumnConfig(baseColumns, draftColumnConfig).map((item) => (
      item.locked ? { ...item, visible: true } : item
    ));
    setColumnConfig(nextConfig);
    setColumnConfigDrawerOpen(false);
    saveColumnConfigToStorage(nextConfig);

    const selectedFields = Array.from(new Set(
      nextConfig
        .filter((item) => item.visible)
        .flatMap((item) => item.fields || (item.field ? [item.field] : []))
        .filter(Boolean)
    )).filter((field) => (
      field !== 'pic' || nextConfig.some((item) => item.key === 'pic' && item.visible)
    ));

    workorderApi.saveDisplayFieldsConfig({
      selectedFields,
    }).catch((error) => {
      console.error('Failed to save workorder display fields remotely:', error);
    });
  }, [baseColumns, draftColumnConfig, messageApi, saveColumnConfigToStorage]);

  const handleColumnConfigReset = useCallback(async () => {
    try {
      const response = await workorderApi.resetDisplayFieldsConfig();
      const remoteData = response?.data?.data || {};
      const resetConfig = applySelectedFieldsToConfig(
        mergeColumnConfig(baseColumns, []),
        remoteData.selectedFields,
        remoteData.fields
      );

      setColumnConfig(resetConfig);
      setDraftColumnConfig(resetConfig);
      setColumnConfigDrawerOpen(false);
      saveColumnConfigToStorage(resetConfig);
      messageApi.success('Table columns reset successfully');
    } catch (error) {
      console.error('Failed to reset workorder display fields remotely:', error);
      messageApi.error(error.response?.data?.message || error.message || 'Failed to reset table columns');
    }
  }, [baseColumns, messageApi, saveColumnConfigToStorage]);

  useEffect(() => {
    const wrapper = tableWrapperRef.current;
    const topScrollbar = topScrollbarRef.current;
    if (!wrapper || !topScrollbar) return undefined;

    let tableScrollContainer = null;

    const syncFromTop = () => {
      if (!tableScrollContainer || syncScrollSourceRef.current === 'table') return;
      syncScrollSourceRef.current = 'top';
      tableScrollContainer.scrollLeft = topScrollbar.scrollLeft;
      syncScrollSourceRef.current = null;
    };

    const syncFromTable = () => {
      if (syncScrollSourceRef.current === 'top') return;
      syncScrollSourceRef.current = 'table';
      topScrollbar.scrollLeft = tableScrollContainer.scrollLeft;
      syncScrollSourceRef.current = null;
    };

    const getTableScrollContainer = () => wrapper.querySelector('.ant-table-body') || wrapper.querySelector('.ant-table-content');

    const updateScrollbar = () => {
      tableScrollContainer = getTableScrollContainer();
      if (!tableScrollContainer) {
        setTopScrollbarWidth(0);
        return;
      }

      const tableElement = tableScrollContainer.querySelector('table');
      setTopScrollbarWidth(tableElement ? tableElement.scrollWidth : tableScrollContainer.scrollWidth);
      topScrollbar.scrollLeft = tableScrollContainer.scrollLeft;
    };

    updateScrollbar();
    topScrollbar.addEventListener('scroll', syncFromTop);

    const bindTableScroll = () => {
      if (!tableScrollContainer) return;
      tableScrollContainer.addEventListener('scroll', syncFromTable);
    };

    const unbindTableScroll = () => {
      if (!tableScrollContainer) return;
      tableScrollContainer.removeEventListener('scroll', syncFromTable);
    };

    bindTableScroll();

    const resizeObserver = new ResizeObserver(() => {
      unbindTableScroll();
      updateScrollbar();
      bindTableScroll();
    });

    resizeObserver.observe(wrapper);

    if (tableScrollContainer) {
      resizeObserver.observe(tableScrollContainer);
    }

    window.addEventListener('resize', updateScrollbar);

    return () => {
      topScrollbar.removeEventListener('scroll', syncFromTop);
      unbindTableScroll();
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateScrollbar);
    };
  }, [workorderData, columns]);





  // 文件详情Modal
  const [isFileModalVisible, setisFileModalVisible] = useState(false)
  const [fileData, setfileData] = useState([])
  // const showFileModal = (documents) => {
  //   setfileData(documents)
  //   setisFileModalVisible(true)
  // }
  const closeFileModal = () => {
    setisFileModalVisible(false)
    setfileData([])
  }



  // 新增状态管理
  // 处理筛选条件点击
  const handleFilterClick = (filterKey) => {
    // 点击卡片筛选时候，下面seacrh 筛选全部重置为默认值
    form.resetFields()
    clearQuickSearchState(false);
    trackWorkorderSearch();

    // 如果点击的是当前激活的筛选条件，则取消激活
    if (activeFilter === filterKey) {
      setActiveFilter(null);
      refreshTableData(null); // 清除筛选

    } else {
      // 否则激活新的筛选条件
      setActiveFilter(filterKey);
      refreshTableData(filterKey);
    }

  };

  // 刷新表格数据的方法
  const refreshTableData = (filter) => {
    console.log('当前筛选条件:', filter);
    // 实现实际的API调用
    if (filter) {
      // 使用nodeNameEn作为processStatus参数
      fetchData({ page: 1, size: 20, processStatus: filter.nodeNameEn });
      setPage(1);
    } else {
      clearTableData(); // 清除筛选时不查询全部工单
      setPage(1);
    }
  };

  // 修改渲染筛选条件卡片
  const renderFilterCard = (filter) => {
    // 根据cardClass确定是进度还是异常类型
    const isException = filter.cardClass === 1;
    // 设置颜色
    const color = isException ? '#dc2626' : '#16a34a';
    // 设置图标
    let icon;
    if (isException) {
      icon = <ExclamationCircleOutlined />;
    } else {
      // 为进度类型设置不同的图标
      switch (filter.id) {
        case 0: // 全部
          icon = <GlobalOutlined />;
          break;
        case 1: // 未分配
          icon = <UserOutlined />;
          break;
        case 2: // 待到港
        case 3: // 已到港
          icon = <TruckOutlined />;
          break;
        case 4: // 待发送A/N
        case 5: // 已发送A/N
          icon = <SendOutlined />;
          break;
        case 6: // 待接收正本提单
          icon = <FileTextOutlined />;
          break;
        case 7: // 待清关
        case 8: // 待通知放行
        case 9: // 待海关放行
        case 10: // 待船司放行
        case 11: // 待码头放行
          icon = <GlobalOutlined />;
          break;
        case 12: // 待铁路运输
          icon = <TrademarkCircleFilled />;
          break;
        case 13: // 待提箱
        case 14: // 已提箱
        case 15: // 待还箱
        case 16: // 已还箱
          icon = <ContainerOutlined />;
          break;
        default:
          icon = <GlobalOutlined />;
      }
    }
    const isActive = activeFilter && activeFilter.id === filter.id;

    return (
      <Col xs={12} sm={12} md={6} lg={6} xl={6} key={filter.id}>
        <Card
          className={`filter-card ${isActive ? 'active' : ''}`}
          size="small"
          hoverable
          onClick={() => handleFilterClick(filter)}
          style={{
            border: isActive ? `2px solid ${color}` : '1px solid #d9d9d9',
            boxShadow: isActive ? `0 4px 12px ${color}20` : '0 2px 4px rgba(0,0,0,0.1)',
            transition: 'all 0.3s ease',
            height: 120
          }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            height: '100%',
            width: '100%',
            padding: '12px 16px',
            boxSizing: 'border-box'
          }}>
            {/* 左侧文字内容 */}
            <div style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              height: '100%'
            }}>
              {/* 中文标题 */}
              <div style={{ marginBottom: '4px' }}>
                <Text strong style={{
                  fontSize: '14px',
                  color: '#262626',
                  lineHeight: '1.2'
                }}>
                  {/* {filter.nodeNameZn} */}
                  {filter.nodeNameEn}
                </Text>
              </div>

              {/* 英文描述 */}
              <div style={{ marginBottom: '8px' }}>
                <Text type="secondary" style={{
                  fontSize: '11px',
                  color: '#8c8c8c',
                  lineHeight: '1.2'
                }}>
                  {filter.nodeNameZn}
                </Text>
              </div>

              {/* 数字 */}
              <div>
                <Text strong style={{
                  fontSize: '16px',
                  color: color,
                  lineHeight: '1.2'
                }}>
                  {filter.count}
                </Text>
              </div>
            </div>

            {/* 右侧图标 */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              backgroundColor: `${color}15`, // 15% 透明度
              marginLeft: '8px'
            }}>
              <div style={{
                color: color,
                fontSize: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {icon}
              </div>
            </div>
          </div>
        </Card>
      </Col>
    );
  };

  // 计算已选择的筛选条件数量
  const getSelectedFilterCount = () => {
    const values = form.getFieldsValue();
    let count = 0;
    Object.keys(values).forEach(key => {
      if (key === 'emptyEmailflag' && (values[key] === '0' || values[key] === 0)) return;
      if (values[key] !== undefined && values[key] !== null && values[key] !== '') {
        count++;
      }
    });
    return count;
  };



  return (
    <div className="workorder-container">
      {contextHolder}

      {/* 复制工单 Modal */}
      <Modal
        title={copyActionType === 'delete' ? 'Delete Workorder' : 'Copy Workorder'}
        open={copyModalOpen}
        onOk={copyActionType === 'delete' ? handleDeleteConfirm : handleCopyConfirm}
        onCancel={resetCopyModalState}
        confirmLoading={copyLoading}
        okText={copyActionType === 'delete' ? 'Delete' : 'Confirm'}
        cancelText="Cancel"
        destroyOnClose
        zIndex={1100}
        okButtonProps={{
          danger: copyActionType === 'delete',
          disabled: !copyDetailData || (copyActionType === 'copy' && !copyHblNo.trim()),
        }}
      >
        <div>
          <div style={{ marginBottom: 8 }}>HBL No.:</div>
          <Space.Compact style={{ width: '100%', marginBottom: 12 }}>
            <Input
              placeholder="Enter HBL No."
              value={copySearchHblNo}
              onChange={(e) => {
                setCopySearchHblNo(e.target.value);
                setCopyDetailData(null);
                if (copyActionType === 'copy') {
                  setCopyHblNo('');
                }
              }}
              onPressEnter={handleSearchCopyTarget}
              autoFocus
            />
            <Button
              type="primary"
              icon={<SearchOutlined />}
              loading={copyDetailLoading}
              onClick={handleSearchCopyTarget}
            >
              Search
            </Button>
          </Space.Compact>

          {copyDetailLoading ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>Loading detail data...</div>
          ) : copyDetailData ? (
            <div>
              <Descriptions
                size="small"
                bordered
                layout="vertical"
                column={3}
                style={{ marginBottom: copyActionType === 'copy' ? 12 : 0 }}
              >
                <Descriptions.Item label="ID">
                  {copyDetailData?.id ?? copyDetailData?.seq ?? '-'}
                </Descriptions.Item>
                <Descriptions.Item label="HBL">
                  {copyDetailData?.hbl_no || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="MBL">
                  {copyDetailData?.mbl_no || '-'}
                </Descriptions.Item>
              </Descriptions>

              {copyActionType === 'copy' && (
                <>
                  <div style={{ marginBottom: 8, color: '#666' }}>
                    Source HBL: <strong>{copyDetailData?.hbl_no || '-'}</strong>
                  </div>
                  <div style={{ marginBottom: 4 }}>New HBL No.:</div>
                  <Input
                    placeholder="Enter new HBL No."
                    value={copyHblNo}
                    onChange={(e) => setCopyHblNo(e.target.value)}
                    onPressEnter={handleCopyConfirm}
                  />
                </>
              )}
            </div>
          ) : null}
        </div>
      </Modal>

      <TableColumnDrawer
        open={columnConfigDrawerOpen}
        columnConfig={draftColumnConfig}
        onClose={() => {
          setColumnConfigDrawerOpen(false);
          setDraftColumnConfig([]);
        }}
        onReset={handleColumnConfigReset}
        onSave={handleColumnConfigSave}
        onDragStart={handleColumnDragStart}
        onDragOver={(event) => event.preventDefault()}
        onDrop={handleColumnDrop}
        onDragEnd={() => {
          draggingColumnKeyRef.current = null;
        }}
        onVisibilityChange={handleColumnVisibilityChange}
      />

      <WorkOrderAttachmentDrawer
        open={attachmentDrawerOpen}
        hblNo={attachmentHblNo}
        onClose={() => {
          setAttachmentDrawerOpen(false);
          setAttachmentHblNo('');
        }}
      />


      {/* 面板控制工具栏 */}
      <div className="panel-toolbar">
        <Tag className="panel-toolbar-tip" color="blue">
          The two buttons on the far right support switching between filtering modes
        </Tag>
        <Tooltip title="Alert">
          <Button
            shape="circle"
            type={!exceptionCollapsed ? 'primary' : 'default'}
            icon={<ExclamationCircleOutlined />}
            onClick={() => setExceptionCollapsed(!exceptionCollapsed)}
          />
        </Tooltip>
        <div className="query-model-switches">
          <div className="query-model-switch">
            <Tooltip title="Search Filters">
              <Button
                className="query-model-search-button"
                shape="circle"
                type={queryModelId === QUERY_MODEL_SEARCH ? 'primary' : 'default'}
                loading={queryModelSaving && queryModelId !== QUERY_MODEL_SEARCH}
                onClick={() => handleQueryModelChange(QUERY_MODEL_SEARCH)}
                aria-label="Search Filters, mode 1"
              >
                <span className="query-model-search-icon"><span className="query-model-search-lens">1</span></span>
              </Button>
            </Tooltip>
          </div>
          <div className="query-model-switch">
            <Tooltip title="Quick Search">
              <Button
                className="query-model-search-button"
                shape="circle"
                type={queryModelId === QUERY_MODEL_QUICK ? 'primary' : 'default'}
                loading={queryModelSaving && queryModelId !== QUERY_MODEL_QUICK}
                onClick={() => handleQueryModelChange(QUERY_MODEL_QUICK)}
                aria-label="Quick Search, mode 2"
              >
                <span className="query-model-search-icon"><span className="query-model-search-lens">2</span></span>
              </Button>
            </Tooltip>
          </div>
        </div>
      </div>

      {/* 筛选条件区域 */}
      {!exceptionCollapsed && (
        <div className="filter-conditions">
          {!exceptionCollapsed && (
            <Card className="filter-section" style={{ marginBottom: 16 }}>
              <div style={{ padding: '8px 4px 4px' }}>
                <Text strong style={{ fontSize: '15px', marginBottom: 10, display: 'block' }}>异常</Text>
                <Row gutter={[12, 12]}>
                  {statusFilters.map(filter => filter.cardClass === 1 && renderFilterCard(filter))}
                </Row>
              </div>
            </Card>
          )}
        </div>
      )}







      {!searchFormCollapsed && (
        <div
          className="search-form"
        >
          <Form
            form={form}
            name="search"
            onFinish={onFinish}
            onReset={onReset}
            layout="horizontal"
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 18 }}

          >


            <div className="form-items"
              style={{
                flexWrap: 'wrap',
                overflow: 'hidden',
                maxHeight: searchFormCollapsed ? '80px' : 'none',
                transition: 'max-height 0.3s ease'
              }}>
              <Form.Item
                name="mblNo"
                label="MBL"
                className="form-item"
              >
                <Input
                  placeholder="Input MBL"
                  allowClear
                />
              </Form.Item>

              <Form.Item
                name="hblNo"
                label="HBL"
                className="form-item"
              >
                <Input
                  placeholder="Input HBL"
                  allowClear
                />
              </Form.Item>

              <Form.Item
                name="containerNo"
                label="Container No"
                className="form-item"
              >
                <Input
                  placeholder="Input Container No"
                  allowClear
                />
              </Form.Item>

              <Form.Item
                name="pic"
                label="PIC"
                className="form-item"
              >
                <Select
                  options={opList}
                  placeholder="Input PIC"
                  allowClear
                  showSearch
                />
              </Form.Item>

              <Form.Item
                name="agent"
                label="Agent"
                className="form-item"
              >
                <Input
                  placeholder="Input Agent"
                  allowClear
                />
              </Form.Item>

              <Form.Item
                name="anStatus"
                label="A/N Status"
                className="form-item"
              >
                <Select
                  placeholder="Select A/N Status"
                  allowClear
                >
                  <Select.Option value="sent">Sent</Select.Option>
                  <Select.Option value="unsent">Unsent</Select.Option>
                  <Select.Option value="anfailed">AN Failed</Select.Option>
                  <Select.Option value="all">All</Select.Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="anDateFlag"
                label=""
                className="form-item form-item--checkbox"
                valuePropName="checked"
                style={{
                  marginTop: 16
                }}
              >
                <Checkbox>NO AN Date</Checkbox>
              </Form.Item>

              <Form.Item
                name="releaseDateFlag"
                label=""
                className="form-item form-item--checkbox"
                valuePropName="checked"
                style={{
                  marginTop: 16
                }}
              >
                <Checkbox>No Release Date</Checkbox>
              </Form.Item>

              <Form.Item
                name="noPicFlag"
                label=""
                className="form-item form-item--checkbox"
                valuePropName="checked"
                style={{
                  marginTop: 16
                }}
              >
                <Checkbox>NO PIC</Checkbox>
              </Form.Item>

              <Form.Item
                name="companyName"
                label="Company Name"
                className="form-item"
              >
                <Input
                  placeholder="Input Company Name"
                  allowClear
                />
              </Form.Item>

              <Form.Item
                name="primeNote"
                label="Prime Note"
                className="form-item"
              >
                <Input
                  placeholder="Input Prime Note"
                  allowClear
                />
              </Form.Item>

              <Form.Item
                name="releaseDateRange"
                label="Release Date Range"
                className="form-item"
              >
                <DatePicker.RangePicker
                  format={DISPLAY_DATE_FORMAT}
                  placeholder={['Start Date', 'End Date']}
                />
              </Form.Item>

              <Form.Item
                name="overdueFlag"
                label=""
                className="form-item form-item--checkbox"
                valuePropName="checked"
                style={{
                  marginTop: 16
                }}
              >
                <Checkbox>Overdue Only</Checkbox>
              </Form.Item>

              {/* 上面重要筛选条件，下方不重要的筛选条件 */}

              <Form.Item
                name="status"
                label="Status"
                className="form-item"
              >
                <Select
                  placeholder="Select Status"
                  allowClear
                >
                  {Object.keys(statusMap).map((status) => (
                    <Select.Option key={status} value={status}>
                      {statusMap[status].text}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="vesselName"
                label="Vessel Name"
                className="form-item"
              >
                <Input
                  placeholder="Input Vessel Name"
                  allowClear
                />
              </Form.Item>

              <Form.Item
                name="portOfDischarge"
                label="Port of Discharge"
                className="form-item"
              >
                <Input
                  placeholder="Input Port of Discharge"
                  allowClear
                />
              </Form.Item>


              <Form.Item
                name="picEmail"
                label="PIC Email"
                className="form-item"
              >
                <Input
                  placeholder="Input PIC Email"
                  allowClear
                />
              </Form.Item>

              {/* <Form.Item
              name="processStatus"
              label="Process Status"
              className="form-item"
            >
              <Input
                placeholder="Input Process Status"
                allowClear
              />
            </Form.Item> */}



              <Form.Item
                name="etd"
                label="ETD"
                className="form-item"
              >
                <DatePicker
                  format={DISPLAY_DATE_FORMAT}
                  placeholder="Select ETD"
                  style={{ width: '100%' }}
                />
              </Form.Item>

              <Form.Item
                name="eta"
                label="ETA"
                className="form-item"
              >
                <DatePicker
                  format={DISPLAY_DATE_FORMAT}
                  placeholder="Select ETA"
                  style={{ width: '100%' }}
                />
              </Form.Item>
              
              <Form.Item
                name="placeOfDelivery"
                label="Place of Delivery"
                className="form-item"
              >
                <Input
                  placeholder="Input Place of Delivery"
                  allowClear
                />
              </Form.Item>

              <Form.Item
                name="voyage"
                label="Voyage"
                className="form-item"
              >
                <Input
                  placeholder="Input Voyage"
                  allowClear
                />
              </Form.Item>

              <Form.Item
                name="blType"
                label="BL Type"
                className="form-item"
              >
                <Input
                  placeholder="Input BL Type"
                  allowClear
                />
              </Form.Item>



              <Form.Item
                name="manStatus"
                label="Manifest Status"
                className="form-item"
              >
                <Select
                  placeholder="Select Manifest Status"
                  allowClear
                >
                  <Select.Option value="not_received">Not Received</Select.Option>
                  <Select.Option value="received">Received</Select.Option>
                  <Select.Option value="all">All</Select.Option>
                </Select>
              </Form.Item>



              <Form.Item
                name="forwardEmail"
                label="Forward Email"
                className="form-item"
              >
                <Select
                  placeholder="Select Forward Email Status"
                  allowClear
                >
                  <Select.Option value="all">All</Select.Option>
                  <Select.Option value="linked">Linked</Select.Option>
                  <Select.Option value="not_linked">Not Linked</Select.Option>
                </Select>
              </Form.Item>



              <Form.Item
                name="zzFlag"
                label=""
                className="form-item form-item--checkbox"
                valuePropName="checked"
                style={{
                  marginTop: 16
                }}
              >
                <Checkbox>ZZ</Checkbox>
              </Form.Item>



              <Form.Item
                name="cashierType"
                label="Cashier"
                className="form-item"
              >
                <Select
                  placeholder="Select Cashier"
                  allowClear
                >
                  <Select.Option value="CS0001">CS0001</Select.Option>
                  <Select.Option value="CS0002">CS0002</Select.Option>
                  <Select.Option value="CS0003">CS0003</Select.Option>
                  <Select.Option value="CS0004">CS0004</Select.Option>
                  <Select.Option value="CS0005">CS0005</Select.Option>
                  <Select.Option value="CS0006">CS0006</Select.Option>
                  <Select.Option value="CS0007">CS0007</Select.Option>
                  <Select.Option value="CS0008">CS0008</Select.Option>
                  <Select.Option value="CS0009">CS0009</Select.Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="emptyEmailflag"
                label="Empty Email"
                className="form-item"
                initialValue="0"
              >
                <Select
                  placeholder="Select Email Status"
                  allowClear
                >
                  <Select.Option value="0">All</Select.Option>
                  <Select.Option value="1">Empty Email</Select.Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="sourceFlag"
                label="Source Flag"
                className="form-item"
              >
                <Select
                  placeholder="Select Source Flag"
                  allowClear
                >
                  <Select.Option value="pms">PMS</Select.Option>
                  <Select.Option value="pai">PAI</Select.Option>
                  {/* <Select.Option value="primeone">PrimeOne</Select.Option> */}
                </Select>
              </Form.Item>


              {/* 加字段 origin note */}
              <Form.Item
                name="originNote"
                label="Origin Note"
                className="form-item"
              >
                <Input
                  placeholder="Input Origin Note"
                  allowClear
                />
              </Form.Item>


            </div>

            <Form.Item>
              <Space
                style={{ marginTop: 4 }}
              >
                <Button type="primary" htmlType="submit">Search</Button>
                <Button htmlType="reset">Reset</Button>

                <span style={{ marginLeft: '20px', padding: '6px 8px', borderRadius: 4, backgroundColor: '#f0f5ff', color: '#1f63fb', fontSize: '13px' }}>
                  {getSelectedFilterCount()} Filters Applied
                </span>
              </Space>
            </Form.Item>
          </Form>
        </div>
      )}



      {/* 快速搜索区域 Quick Search */}
      {!quickSearchCollapsed && (
        <div className="quick-search-form" style={{ marginBottom: 12 }}>
          <div style={{ padding: '8px 12px 0 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div className="quick-search-header">
              {/* <Text strong style={{ fontSize: '14px', color: '#1f63fb' }}>Quick Search</Text> */}
              {hasQuickSearchHistory && (
                <div className="quick-search-history-toolbar">
                  <div className="quick-search-history-trail">
                    {quickSearchHistory.map((item, index) => (
                      <React.Fragment key={item.id}>
                        <span className={`quick-search-history-item ${index === quickSearchHistoryIndex ? 'active' : ''}`}>
                          {item.label}
                        </span>
                        {index < quickSearchHistory.length - 1 && (
                          <span className="quick-search-history-separator">&gt;</span>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>


          <Form
            form={quickSearchForm}
            name="quickSearch"
            initialValues={{ type: 'hblNo' }}
            layout="vertical"
            style={{ 
              padding: '8px 12px 12px',
              display: 'flex', 
              gap: '10px',
              alignItems: 'flex-start', 
              flexWrap: 'nowrap', 
             }}
          >
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'nowrap', overflowX: 'auto' }}>
              <Form.Item name="type" label="Type" style={{ marginBottom: 4, minWidth: 140, width: 140 }}>
                <Select
                  placeholder="Select Type"
                  allowClear
                  style={{
                    height: 26,
                    width: '100%',
                  }}
                  options={[
                    { value: 'hblNo', label: 'HBL' },
                    { value: 'primeNote', label: 'Prime Note' },
                    { value: 'anyWord', label: 'Any Word' },
                    { value: 'mblNo', label: 'MBL' },
                    { value: 'containerNo', label: 'Container No' },
                    { value: 'pic', label: 'PIC' },
                    { value: 'placeOfDelivery', label: 'Place of Delivery' },
                    { value: 'portOfDischarge', label: 'Port of Discharge' },
                    { value: 'portOfLoading', label: 'Port of Loading' },
                    { value: 'voyage', label: 'Voyage' },
                    { value: 'vesselName', label: 'Vessel Name' },
                    { value: 'totalCc', label: 'Total CC' },
                    { value: 'mblCc', label: 'MBL CC' },
                    { value: 'originNote', label: 'Origin Note' },
                    { value: 'customerNote', label: 'Customer Note' },
                    { value: 'serialNo', label: 'Serial Number' },

                    { value: 'bookingNo', label: 'BK No.' },
                    { value: 'carrier', label: 'Carrier' },
                    { value: 'eta', label: 'ETA' },
                    { value: 'etd', label: 'ETD' },
                    { value: 'totalPackages', label: 'Total PKGS' },
                    { value: 'cashierType', label: 'Cashier' },
                  ]}
                />
              </Form.Item>

              <Form.Item name="searchText" label="Search Text" style={{ marginBottom: 4, minWidth: 180, width: 180 }}>
                <Input
                  placeholder="Input search text"
                  style={{
                    height: 26,
                    width: '100%',
                  }}
                  allowClear
                />
              </Form.Item>

              <Form.Item name="fromDate" label="From Date" style={{ marginBottom: 4, minWidth: 96, width: 96 }}>
                <DatePicker
                  format={DISPLAY_DATE_FORMAT}
                  placeholder="From"
                  style={{ width: 96, height: 26 }}
                />
              </Form.Item>

              <Form.Item name="toDate" label="To Date" style={{ marginBottom: 4, minWidth: 96, width: 96 }}>
                <DatePicker
                  format={DISPLAY_DATE_FORMAT}
                  placeholder="To"
                  style={{ width: 96, height: 26 }}
                />
              </Form.Item>

              <Form.Item name="status" label="Status" style={{ marginBottom: 4, minWidth: 140, width: 140 }}>
                <Select
                  placeholder="Select Status"
                  allowClear
                  style={{
                    height: 26,
                    width: '100%',
                  }}
                  options={[
                    { value: 'ZZ', label: 'ZZ' },
                    { value: 'Transmitted', label: 'TRANSMITTED' },
                    { value: 'Process', label: 'PROCESSED' },
                    { value: 'released', label: 'RELEASED' },
                    { value: 'onHold', label: 'ON HOLD' },
                    { value: 'overdue', label: 'OVERDUE' },
                    { value: 'anSent', label: 'A/N SENT' },
                    { value: 'anFailed', label: 'AN FAILED' },
                    { value: 'hblOriginal', label: 'HBL ORIGINAL' },
                    { value: 'hblTelex', label: 'HBL TELEX' },
                    { value: 'manifestRcvd', label: 'MANIFEST RCVD' },
                    { value: 'mblOriginal', label: 'MBL ORIGINAL' },
                    { value: 'mblTelex', label: 'MBL TELEX' },
                    { value: 'onBcList', label: 'ON BC LIST' },
                  ]}
                />
              </Form.Item>

              <Form.Item name="noInfo" label="No Info" style={{ marginBottom: 4, minWidth: 150 }}>
                <Select
                  placeholder="Select"
                  allowClear
                  style={{
                    height: 26
                  }}
                  options={[
                    { value: 1, label: 'NO A/N DATE' },
                    { value: 3, label: 'NO RELEASE DATE' },
                    { value: 8, label: 'NO PIC' },
                    { value: 2, label: 'NO ETA DATE' },
                    { value: 4, label: 'NO PICK-UP#' },
                    { value: 5, label: 'NO IT#' },
                    { value: 6, label: 'NO FREIGHT LOCATION' },
                    { value: 7, label: 'NO EML ADDRESS' },
                  ]}
                />
              </Form.Item>
            </div>

            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', justifyContent: 'flex-start' }}>
              {/* SEARCH 区域: label 在上，按钮在下 */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                <span style={{ marginBottom: 8,fontSize: 12, color: '#333', fontWeight: 500 }}>Search</span>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                  <Button 
                    type="primary"
                    style={{
                      width: 100
                    }}
                    onClick={() => handleQuickSearch('pai')}>
                      Search (PAI)
                  </Button>
                  <Button type="primary" onClick={() => handleQuickSearch('pms')} style={{ width: 100, backgroundColor: '#722ed1' }}>Search (PMS)</Button>
                  <Button type="primary" onClick={() => handleQuickSearch('all')} style={{ width: 100, backgroundColor: '#13c2c2' }}>Search (ALL)</Button>
                </div>
              </div>

              <div className="quick-search-side-actions">
                <Button
                  onClick={handleQuickSearchReset}
                  style={{ 
                    marginTop: 32,
                    width: 80
                  }}
                >
                  Reset
                </Button>
                <Button
                  icon={<LeftOutlined />}
                  style={{
                    width: 80
                  }}
                  disabled={quickSearchHistoryIndex <= 0}
                  onClick={() => handleQuickSearchHistoryStep(-1)}
                >
                  Back
                </Button>
              </div>
            </div>
          </Form>
        </div>
      )}


      <div className="workorder-table">
        <div
          className="workorder-table-tools"
          style={{ display: 'flex', justifyContent: 'space-between' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Button
              type="primary"
              onClick={() => navigate('/workordercreate')}
            >Create
            </Button>
            {(userInfo?.userRole === "Admin" ||
              userInfo?.userRole === "OP" ||
              userInfo?.userRole === "Manager") && (
              <Tooltip title="Copy">
                <Button
                  type="default"
                  icon={<CopyOutlined />}
                  onClick={() => handleOpenCopyModal('copy')}
                >
                  Copy
                </Button>
              </Tooltip>
            )}
            {canDeleteWorkorder && (
              <Button
                type="default"
                icon={<DeleteFilled />}
                danger
                onClick={() => handleOpenCopyModal('delete')}
              >
                Delete
              </Button>
            )}
          </div>

          <div
            style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '6px 0 12px 0' }}
          >
            {userInfo?.canExport === 1 && (
              <>
                <Button
                  type="default"
                  onClick={() => exportShipmentsandFreightCharges(null)}
                >Shipments
                </Button>
                <Button
                  type="default"
                  onClick={() => exportShipmentsandFreightCharges(1)}
                >Freight Charges
                </Button>
              </>
            )}
            <Tooltip title="Configure Columns">
              <Button
                type="default"
                shape="circle"
                icon={<TableOutlined />}
                onClick={openColumnConfigDrawer}
              />
            </Tooltip>
          </div>


        </div>
        <div className="workorder-table-scroll-shell" ref={tableWrapperRef}>
          <div
            className="workorder-table-top-scrollbar"
            ref={topScrollbarRef}
          >
            <div
              className="workorder-table-top-scrollbar-inner"
              style={{ width: topScrollbarWidth || '100%' }}
            />
          </div>
          <Table
            loading={loading}
            className='workorder-table-table'
            components={{ header: { cell: ResizableHeaderCell } }}
            dataSource={workorderData.map((item) => ({ ...item, key: item.seq }))}
            rowKey='seq'
            columns={columns}
            size="small"
            rowClassName={(_, index) => (index % 2 === 0 ? 'workorder-table-row-even' : 'workorder-table-row-odd')}
            scroll={{ x: 'max-content', y: 820 }}

            pagination={{
              total,
              showTotal: (total) => `Total ${total} Orders`,
              pageSize,
              current: page,
              showQuickJumper: true,
              showSizeChanger: true,
              // onChange: (newPage, newPageSize) => {
              //   setPage(newPage);
              //   setPageSize(newPageSize);
              //   const formValues = form.getFieldsValue();
              //   fetchData({ page: newPage, size: newPageSize, ...formValues });
              // },


              onChange: (newPage, newPageSize) => {
                setPage(newPage);
                setPageSize(newPageSize);

                if (hasQuickSearchHistory) {
                  runQuickSearchHistory(quickSearchHistory, quickSearchHistoryIndex, newPage, newPageSize);
                  return;
                }

                const formValues = form.getFieldsValue();

                // ===== 复制 onFinish 的参数处理逻辑 =====

                const v = { ...(formValues || {}) };

                // 'all' 不参与查询
                const dropIfAll = (val) => {
                  if (val == null) return undefined;
                  const s = String(val).trim();
                  return s.toLowerCase() === 'all' ? undefined : s;
                };

              // 日期格式
              const fmtDate = (d) => (d ? dayjs(d).format('YYYY-MM-DD') : undefined);

              // pic: 'Unassigned' -> null；'all' -> undefined
              let pic = v.pic;
              if (pic != null && String(pic).trim().toLowerCase() === 'unassigned') {
                pic = null;
              } else {
                pic = dropIfAll(pic);
              }

              // manStatus: not_received->0, received->1, all->undefined
              let manStatus;
              if (v.manStatus != null) {
                const ms = String(v.manStatus).toLowerCase();
                if (ms === 'not_received') manStatus = 0;
                else if (ms === 'received') manStatus = 1;
              }

              // forwardEmail: 'all' -> undefined；其他透传
              let forwardEmail;
              if (v.forwardEmail != null) {
                const fe = String(v.forwardEmail).toLowerCase();
                forwardEmail = fe === 'all' ? undefined : fe;
              }

              // 处理 flag 字段 - 转换为数字 0 或 1
              const anDateFlag = v.anDateFlag === true ? 1 : (v.anDateFlag === false ? 0 : undefined);
              const releaseDateFlag = v.releaseDateFlag === true ? 0 : undefined;
              const zzFlag = v.zzFlag === true ? 1 : (v.zzFlag === false ? 0 : undefined);
              const noPicFlag = v.noPicFlag === true ? 1 : (v.noPicFlag === false ? 0 : undefined);
              const overdueFlag = v.overdueFlag === true ? 1 : (v.overdueFlag === false ? 0 : undefined);

              // 处理 releaseDateRange -> releaseDateStart / releaseDateEnd
              let releaseDateStart;
              let releaseDateEnd;
              if (v.releaseDateRange && v.releaseDateRange.length === 2 && v.releaseDateRange[0] && v.releaseDateRange[1]) {
                releaseDateStart = dayjs(v.releaseDateRange[0]).format('YYYY-MM-DD');
                releaseDateEnd = dayjs(v.releaseDateRange[1]).format('YYYY-MM-DD');
              }

              // 组装参数
              const params = {
                page: newPage,
                size: newPageSize,

                hblNo: dropIfAll(v.hblNo),
                mblNo: dropIfAll(v.mblNo),
                status: dropIfAll(v.status),
                vesselName: dropIfAll(v.vesselName),
                portOfDischarge: dropIfAll(v.portOfDischarge),
                placeOfDelivery: dropIfAll(v.placeOfDelivery),
                picEmail: dropIfAll(v.picEmail),
                containerNo: dropIfAll(v.containerNo),
                voyage: dropIfAll(v.voyage),
                blType: dropIfAll(v.blType),
                agent: dropIfAll(v.agent),
                companyName: dropIfAll(v.companyName),
                primeNote: dropIfAll(v.primeNote),
                originNote: dropIfAll(v.originNote),

                pic,
                manStatus,
                anStatus: v.anStatus,
                forwardEmail,
                anDateFlag,
                releaseDateFlag,
                zzFlag,
                noPicFlag,
                overdueFlag,
                emptyEmailflag: getEmptyEmailflag(v.emptyEmailflag),

                cashierType: dropIfAll(v.cashierType),

                releaseDateStart,
                releaseDateEnd,

                etd: fmtDate(v.etd),
                eta: fmtDate(v.eta),
                sourceFlag: dropIfAll(v.sourceFlag),
              };

              // 去掉 undefined 字段
              Object.keys(params).forEach((k) => params[k] === undefined && delete params[k]);

              // 如果当前有选中的筛选卡片，把它的 nodeNameEn 当成 processStatus 传过去
              if (activeFilter && activeFilter.nodeNameEn) {
                params.processStatus = activeFilter.nodeNameEn;
              }

              fetchData(params);
              },
            }}
          />
        </div>
      </div>

      <Modal
        title={`Container Details${activeContainerRecord?.hblNo ? ` - ${activeContainerRecord.hblNo}` : ''}`}
        open={containerModalOpen}
        width={920}
        zIndex={3000}
        getContainer={() => document.body}
        onCancel={() => {
          setContainerModalOpen(false);
          setActiveContainerRecord(null);
        }}
        footer={[
          <Button key="cancel" icon={<CloseOutlined />} onClick={() => {
            setContainerModalOpen(false);
            setActiveContainerRecord(null);
          }} />,
          <Button
            key="save"
            type="primary"
            icon={<SaveOutlined />}
            loading={savingContainerSeq === activeContainerRecord?.seq}
            disabled={!activeContainerRecord}
            onClick={() => activeContainerRecord && saveContainerFields(activeContainerRecord)}
          />
        ]}
      >
        <Table
          size="small"
          rowKey={(row) => row.id}
          pagination={false}
          dataSource={activeContainerRecord ? (containerEditValues[activeContainerRecord.seq] || (activeContainerRecord.containers || []).map(mapContainerToEditable)) : []}
          columns={[
            { title: 'Container No.', dataIndex: 'containerNo', key: 'containerNo', width: 180 },
            {
              title: 'LFD', dataIndex: 'lastFreeDay', key: 'lastFreeDay', width: 170,
              render: (value, row) => (
                <DatePicker allowClear format={DISPLAY_DATE_FORMAT} value={toDatePickerValue(value)} onChange={(date) => activeContainerRecord && handleContainerDateChange(activeContainerRecord.seq, row.id, 'lastFreeDay', date)} />
              )
            },
            {
              title: 'Pick Up Date', dataIndex: 'pickDate', key: 'pickDate', width: 170,
              render: (value, row) => (
                <DatePicker allowClear format={DISPLAY_DATE_FORMAT} value={toDatePickerValue(value)} onChange={(date) => activeContainerRecord && handleContainerDateChange(activeContainerRecord.seq, row.id, 'pickDate', date)} />
              )
            },
            {
              title: 'Empty Return Date', dataIndex: 'returnDate', key: 'returnDate', width: 190,
              render: (value, row) => (
                <DatePicker allowClear format={DISPLAY_DATE_FORMAT} value={toDatePickerValue(value)} onChange={(date) => activeContainerRecord && handleContainerDateChange(activeContainerRecord.seq, row.id, 'returnDate', date)} />
              )
            },
          ]}
        />
      </Modal>

      {/* 数据详情 Modal
      <WorkOrderDetailModal
        isInfoModalVisible={isInfoModalVisible}
        infoData={selectedRecord}
        handleCloseInfoModal={handleCloseInfoModal}
      /> */}

      {/* 查看文件的Modal */}
      <ViewFileModal
        isFileModalVisible={isFileModalVisible}
        fileData={fileData}
        closeFileModal={closeFileModal}
      />

    </div>
  );
}


export default WorkOrder;
